import { Component, signal, computed, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ReservationEditService } from '../../services/reservation-edit.service';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { validateAndFocusFirstError, focusFieldWithError } from '../../utils/form-validation';
import { finishLoadingAfter } from '../../utils/loading';
import { friendlyError } from '../../utils/friendly-error';
import { formatClockTime } from '../../utils/format';
import { ReservationService } from '../../services/reservation.service';
import { CustomerService } from '../../services/customer.service';
import { SalaService } from '../../services/sala.service';
import { AuthService } from '../../services/auth.service';
import { 
  CreateReservationForUserRequest,
  CreateReservationForGuestRequest,
  Reservation,
  Espacio,
  Cliente,
  EspacioExtra,
  UpdateReservationRequest,
  UpdateReservationGroupRequest
} from '../../models/sala.model';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { UiSkeletonComponent } from '../ui/ui-skeleton.component';
import { SpaceScheduleService } from '../../services/space-schedule.service';
import { SpaceSchedule } from '../../models/sala.model';
import { DateTimePickerDirective } from '../../directives/date-time-picker.directive';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';
import { PaginationComponent } from '../ui/pagination.component';
import { AdminTabOption, AdminTabsComponent } from '../ui/admin-tabs.component';
import { ManagementRecordCardComponent } from '../ui/management-record-card.component';
import { UiStateComponent } from '../ui/ui-state.component';

@Component({
  selector: 'app-reservas',
  standalone: true,
  imports: [CommonModule, FormsModule, UiSkeletonComponent, DateTimePickerDirective, PaginationComponent, AdminTabsComponent, ManagementRecordCardComponent, UiStateComponent, CooldownBubbleDirective],
  template: `
    <div class="reservas-container paginated-module">
      <div class="reservas-filter-panel">
        <div class="reservation-period">
          <label>Buscar
            <input
              type="text"
              [ngModel]="searchTerm()"
              (ngModelChange)="onSearchChange($event)"
              (keyup.enter)="applyFilters()"
              placeholder="Nombre, correo, teléfono o referencia (R-0001 / 1)"
            >
          </label>
          <label>Espacio
            <select [ngModel]="selectedSpaceId()" (ngModelChange)="onSpaceFilterChange($event)">
              <option [ngValue]="null">Todos</option>
              <option *ngFor="let espacio of salaService.espacios()" [ngValue]="espacio.space_id">{{ espacio.title }}</option>
            </select>
          </label>
          <label>Pago
            <select [ngModel]="selectedPaymentStatus()" (ngModelChange)="onPaymentStatusChange($event)">
              <option value="">Todos</option>
              <option value="paid">Pagado</option>
              <option value="pending">Pendiente</option>
              <option value="failed">Fallido</option>
            </select>
          </label>
          <label>Mes
            <select [ngModel]="selectedMonth()" (ngModelChange)="onMonthChange($event)">
              <option [ngValue]="0">Todos</option>
              <option *ngFor="let month of months; let i = index" [ngValue]="i + 1">{{ month }}</option>
            </select>
          </label>
          <label>Año
            <input type="number" min="2020" max="2100" [ngModel]="selectedYear()" (ngModelChange)="onYearChange($event)" placeholder="Todos">
          </label>
          <button type="button" class="btn-refresh" (click)="applyFilters()" [disabled]="isLoading()" cooldownBubble data-cooldown-key="res-filter">
            <span class="refresh-spinner" *ngIf="isLoading()" aria-hidden="true"></span>
            {{ isLoading() ? 'Filtrando…' : 'Aplicar filtros' }}
          </button>
        </div>
      </div>
      <app-admin-tabs
        class="reservas-status-tabs"
        [tabs]="reservationStatusTabs"
        [active]="selectedStatus()"
        ariaLabel="Filtrar reservas por estado"
        (tabSelect)="onStatusChange($event)"
      ></app-admin-tabs>


      <!-- Formulario de creación -->
      <div *ngIf="showCreateForm()" class="reservation-modal-overlay" (click)="cancelForm()">
       <div class="form-container create-reservation-modal" (click)="$event.stopPropagation()">
        <div class="form-header">
          <h3>Nueva Reserva</h3>
          <button (click)="cancelForm()" class="close-btn">×</button>
        </div>

        <!-- Selector de tipo de reservación -->
        <div class="form-group">
          <label>Tipo de Reservación *</label>
          <div class="radio-group">
            <label class="radio-label">
              <input 
                type="radio" 
                name="reservationType" 
                value="user"
                [(ngModel)]="reservationType"
                (change)="onReservationTypeChange()"
              />
              <span>Usuario Registrado</span>
            </label>
            <label class="radio-label">
          <input 
                type="radio" 
                name="reservationType" 
                value="guest"
                [(ngModel)]="reservationType"
                (change)="onReservationTypeChange()"
              />
              <span>Invitado</span>
            </label>
          </div>
        </div>
        
        <form (ngSubmit)="onSubmit()" class="reserva-form" #reservaFormRef="ngForm" #reservaFormEl novalidate>
          <!-- Selección de usuario registrado -->
          <div *ngIf="reservationType === 'user'" class="form-group">
            <label for="userId">Cliente Registrado *</label>
          <select 
              id="userId" 
              [(ngModel)]="reservationForm.user_id" 
              name="userId"
              required
            class="form-control"
          >
              <option value="">Seleccionar cliente</option>
              <option *ngFor="let cliente of customerService.clientes()" [value]="cliente.user_id">
                {{ cliente.name }} - {{ cliente.email }}
              </option>
          </select>
        </div>
        
          <!-- Datos de invitado -->
          <div *ngIf="reservationType === 'guest'" class="form-row">
            <div class="form-group">
              <label for="guestName">Nombre del Invitado *</label>
              <input 
                type="text" 
                id="guestName" 
                [(ngModel)]="reservationForm.guest_name" 
                name="guestName"
                required
                class="form-control"
                placeholder="Ej: Juan Pérez"
              />
            </div>
            <div class="form-group">
              <label for="guestPhone">Teléfono *</label>
              <input 
                type="tel" 
                id="guestPhone" 
                [(ngModel)]="reservationForm.guest_phone" 
                name="guestPhone"
                required
                class="form-control"
                placeholder="+503 1234-5678"
              />
            </div>
          </div>
          
          <div *ngIf="reservationType === 'guest'" class="form-group">
            <label for="guestEmail">Email</label>
            <input 
              type="email" 
              id="guestEmail" 
              [(ngModel)]="reservationForm.guest_email" 
              name="guestEmail"
              class="form-control"
              placeholder="juan.perez@example.com"
            />
          </div>

          <!-- Selección de espacio -->
          <div class="form-group">
            <label for="spaceId">Espacio *</label>
            <select 
              id="spaceId" 
              [(ngModel)]="reservationForm.space_id" 
              name="spaceId"
              required
              class="form-control"
              (change)="onSpaceChange()"
            >
              <option value="">Seleccionar espacio</option>
              <option *ngFor="let espacio of salaService.espacios()" [value]="espacio.space_id">
                {{ espacio.title }} (Capacidad: {{ espacio.capacity }})
              </option>
            </select>
          </div>

          <!-- Fecha y horario -->
          <div class="form-group multi-date-toggle-row">
            <label class="multi-date-toggle">
              <input type="checkbox" [(ngModel)]="multiDateMode" name="multiDateMode" (change)="onMultiDateModeChange()">
              <span>Reservar varios días (mismo horario elegido abajo, un solo pago y comprobante)</span>
            </label>
          </div>
          <div class="form-row">
            <div class="form-group" *ngIf="!multiDateMode">
              <label for="reservationDate"><i class="bi bi-calendar3" aria-hidden="true"></i> Fecha *</label>
              <input
                type="text"
                id="reservationDate"
                [(ngModel)]="reservationForm.reservation_date"
                name="reservationDate"
                required
                class="form-control"
                appDatePicker="date"
                [dayOccupancy]="dayOccupancyMap"
                [pickerDisabled]="isScheduleLoading() || !reservationForm.space_id"
                (pickerValueChange)="onReservationDateChange($event)"
                placeholder="Seleccionar fecha"
              />
              <small class="availability-help" *ngIf="!reservationForm.space_id">Selecciona primero un espacio.</small>
              <small class="availability-help" *ngIf="isScheduleLoading()">Consultando días disponibles…</small>
            </div>
            <div class="form-group" *ngIf="multiDateMode">
              <label for="reservationDates"><i class="bi bi-calendar3" aria-hidden="true"></i> Días *</label>
              <input
                type="text"
                id="reservationDates"
                [ngModel]="reservationDatesInput"
                (ngModelChange)="reservationDatesInput = $event"
                name="reservationDates"
                required
                class="form-control"
                appDatePicker="date"
                [multiple]="true"
                [availableDays]="undefined"
                [dayOccupancy]="dayOccupancyMap"
                [pickerDisabled]="isScheduleLoading() || !reservationForm.space_id"
                (datesValueChange)="onReservationDatesChange($event)"
                placeholder="Seleccionar días"
              />
              <small class="availability-help" *ngIf="!reservationForm.space_id">Selecciona primero un espacio.</small>
              <small class="availability-help" *ngIf="isScheduleLoading()">Consultando días disponibles…</small>
              <small class="availability-help" *ngIf="reservationForm.reservation_dates.length">{{ reservationForm.reservation_dates.length }} día(s) seleccionado(s). Puedes incluir hasta un año completo en la misma reserva.</small>
            </div>
            <div class="form-group">
              <label for="numberOfPeople">Número de Personas *</label>
              <input placeholder="Ej: 4" 
                type="number" 
                id="numberOfPeople" 
                [(ngModel)]="reservationForm.number_of_people" 
                name="numberOfPeople"
                required
                min="1"
                [max]="selectedSpace()?.capacity || null"
                class="form-control"
              />
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="startTime"><i class="bi bi-clock" aria-hidden="true"></i> Hora de Inicio *</label>
              <input 
                type="text" 
                id="startTime" 
                [(ngModel)]="reservationForm.start_time" 
                name="startTime"
                required
                class="form-control"
                appDatePicker="time"
                [minTime]="selectedOpeningTime"
                [maxTime]="selectedClosingTime"
                [pickerDisabled]="!selectedDaySchedule"
                (pickerValueChange)="onReservationTimeChange()"
                placeholder="Hora de inicio"
              />
            </div>
            <div class="form-group">
              <label for="endTime"><i class="bi bi-clock" aria-hidden="true"></i> Hora de Fin *</label>
              <input 
                type="text" 
                id="endTime" 
                [(ngModel)]="reservationForm.end_time" [afterTime]="reservationForm.start_time" 
                name="endTime"
                required
                class="form-control"
                appDatePicker="time"
                [minTime]="selectedOpeningTime"
                [maxTime]="selectedClosingTime"
                [pickerDisabled]="!selectedDaySchedule"
                (pickerValueChange)="onReservationTimeChange()"
                placeholder="Hora de fin"
              />
            </div>
          </div>
          <div class="availability-summary" *ngIf="!multiDateMode && reservationForm.reservation_date">
            <ng-container *ngIf="selectedDaySchedule; else closedDay">
              <i class="bi bi-clock" aria-hidden="true"></i>
              Horario disponible: {{ formatClockTime(selectedOpeningTime) }} – {{ formatClockTime(selectedClosingTime) }}.
              Los selectores avanzan en intervalos de 30 minutos.
            </ng-container>
            <ng-template #closedDay>
              <i class="bi bi-calendar-x" aria-hidden="true"></i>
              El espacio no está disponible en el día seleccionado.
            </ng-template>
          </div>
          <div class="availability-summary" *ngIf="multiDateMode && reservationForm.reservation_dates.length" [class.availability-error]="!!multiDateScheduleError">
            <ng-container *ngIf="!multiDateScheduleError; else multiDateError">
              <i class="bi bi-clock" aria-hidden="true"></i>
              Horario disponible en los días elegidos: {{ formatClockTime(selectedOpeningTime) }} – {{ formatClockTime(selectedClosingTime) }} (intersección de todos los días seleccionados).
            </ng-container>
            <ng-template #multiDateError>
              <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
              {{ multiDateScheduleError }}
            </ng-template>
          </div>
          <div class="availability-summary total-price-preview" *ngIf="multiDateMode && multiDateEstimatedTotal() > 0">
            <i class="bi bi-cash-coin" aria-hidden="true"></i>
            Total estimado por {{ reservationForm.reservation_dates.length }} día(s): <strong>{{ multiDateEstimatedTotal() | currency:'USD' }}</strong>
            <span *ngIf="multiDateEstimatedHasDiscount" class="price-preview-tag"><i class="bi bi-tag-fill" aria-hidden="true"></i> Tarifa especial aplicada</span>
          </div>
          <div class="availability-summary total-price-preview" *ngIf="!multiDateMode && singleDateEstimatedTotal() > 0">
            <i class="bi bi-cash-coin" aria-hidden="true"></i>
            Total estimado: <strong>{{ singleDateEstimatedTotal() | currency:'USD' }}</strong>
            <span *ngIf="multiDateEstimatedHasDiscount" class="price-preview-tag"><i class="bi bi-tag-fill" aria-hidden="true"></i> Tarifa especial aplicada</span>
          </div>

          <div class="manual-pricing-panel">
            <strong><i class="bi bi-sliders"></i> Precio personalizado (opcional)</strong>
            <small>Solo aplica a esta reserva manual. Si se deja vacío, se usa el precio del espacio. El subtotal indicado corresponde a todo el conjunto; los extras se agregan aparte.</small>
            <div class="form-row">
              <div class="form-group">
                <label for="customPriceTotal">Subtotal personalizado del conjunto</label>
                <input type="number" id="customPriceTotal" name="customPriceTotal" [(ngModel)]="reservationForm.custom_price_total" min="0" step="0.01" class="form-control" placeholder="Dejar vacío para usar la tarifa">
              </div>
              <div class="form-group">
              </div>
            </div>
            <div class="availability-summary" *ngIf="customPricingPreview() as preview">
              Subtotal por día: <strong>{{ preview.basePerDay | currency:'USD' }}</strong>
              · Total del conjunto: <strong>{{ preview.total | currency:'USD' }}</strong>
            </div>
          </div>

          <!-- Extras -->
          <div *ngIf="selectedSpaceExtras().length > 0" class="form-group">
            <label>Extras Disponibles</label>
            <div class="extras-list">
              <div *ngFor="let extra of selectedSpaceExtras()" class="extra-item">
                <label class="extra-checkbox" *ngIf="extra.extra_id">
                  <input 
                    type="checkbox" 
                    [value]="extra.extra_id"
                    (change)="toggleExtra(extra.extra_id!, $event)"
                  />
                  <span>{{ extra.name }} - {{ '$' + extra.price }}</span>
                </label>
            <input
                  *ngIf="extra.extra_id && isExtraSelected(extra.extra_id)"
                  type="number"
                  [ngModel]="selectedExtrasMap[extra.extra_id]"
                  (ngModelChange)="updateExtraQuantity(extra.extra_id, $event)"
                  [name]="'extraQty' + extra.extra_id"
                  min="1"
                  class="extra-quantity" [attr.max]="extra.max_quantity || null" [attr.data-max-message]="extra.max_quantity ? 'Máximo alcanzado: ' + extra.max_quantity + ' por reserva.' : null"
                  placeholder="Cantidad"
                />
          </div>
          </div>
          </div>

          <div class="form-group">
            <label>Estado de pago</label>
            <div class="payment-status-choice">
              <label class="radio-pill">
                <input type="radio" name="createPaymentStatus" value="pending" [(ngModel)]="reservationForm.payment_status">
                <span><i class="bi bi-clock"></i> Pendiente (enviar enlace de pago)</span>
              </label>
              <label class="radio-pill">
                <input type="radio" name="createPaymentStatus" value="paid" [(ngModel)]="reservationForm.payment_status">
                <span><i class="bi bi-check-circle"></i> Pagada (pago recibido por otro medio)</span>
              </label>
            </div>
          </div>

          <div *ngIf="reservationForm.payment_status === 'paid'" class="external-payment-fields">
            <div class="external-payment-header">
              <i class="bi bi-cash-coin" aria-hidden="true"></i>
              <div>
                <strong>Confirmar pago externo</strong>
                <span>El cliente pagó al negocio por otro medio (no por la pasarela de TuSala).</span>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Método de pago externo *</label>
                <select [(ngModel)]="reservationForm.external_payment_method" name="createExternalMethod" class="form-control" required>
                  <option value="">Selecciona el método</option>
                  <option value="cash">Efectivo</option>
                  <option value="bank_transfer">Transferencia bancaria</option>
                  <option value="cheque">Cheque</option>
                  <option value="card_offline">Tarjeta (fuera de línea)</option>
                  <option value="other">Otro</option>
                </select>
              </div>
              <div class="form-group">
                <label><i class="bi bi-calendar3" aria-hidden="true"></i> Fecha del pago</label>
                <input type="text" [(ngModel)]="reservationForm.external_paid_at" name="createExternalPaidAt" class="form-control" appDatePicker="date" placeholder="Seleccionar fecha" autocomplete="off">
              </div>
            </div>
            <div class="form-group">
              <label>Comprobante de pago (foto o PDF)</label>
              <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" (change)="onReceiptFileChange($event, 'create')" class="form-control">
              <ul *ngIf="reservationForm.receipts?.length" class="receipt-list">
                <li *ngFor="let rc of reservationForm.receipts; let i = index">
                  <i class="bi bi-paperclip"></i> {{ rc.original_name || rc.file_name }}
                  <button type="button" class="receipt-remove" (click)="removeReceipt(i, 'create')" aria-label="Quitar comprobante">&times;</button>
                </li>
              </ul>
            </div>
            <div class="form-group">
              <label>Notas (opcional)</label>
              <textarea [(ngModel)]="reservationForm.external_notes" name="createExternalNotes" class="form-control" rows="2" maxlength="500" placeholder="Observaciones del pago"></textarea>
            </div>
            <div class="external-payment-notice">
              <i class="bi bi-check-circle" aria-hidden="true"></i>
              <span>Al guardar, la reserva quedará registrada como <strong>pagada</strong> y se le enviará al cliente un correo de pago confirmado con el comprobante. No se generará enlace de pago.</span>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" (click)="cancelForm()" class="btn-secondary">
              Cancelar
            </button>
            <button type="submit" class="btn-primary" [disabled]="isCreating()" cooldownBubble data-cooldown-key="create-reservation">
              {{ isCreating() ? 'Creando...' : 'Crear Reserva' }}
            </button>
          </div>
        </form>
       </div>
      </div>

      <div *ngIf="editingReservation()" class="reservation-modal-overlay reservation-modal-overlay--elevated" (click)="closeEditReservation()">
        <section class="form-container edit-reservation-modal" (click)="$event.stopPropagation()">
          <div class="form-header">
            <div><small>RESERVA {{ getReservationReference(editingReservation()) }}</small><h3>Editar reservación</h3></div>
            <button type="button" (click)="closeEditReservation()" class="close-btn">×</button>
          </div>
          <form (ngSubmit)="saveReservation()" class="reserva-form" #editFormRef="ngForm" #editFormEl novalidate>
            <div class="form-group">
              <label>Espacio *</label>
              <select [(ngModel)]="editForm.space_id" name="editSpace" class="form-control" required (change)="onEditSpaceChange()">
                <option *ngFor="let espacio of salaService.espacios()" [ngValue]="espacio.space_id">{{ espacio.title }} · {{ espacio.capacity }} personas</option>
              </select>
            </div>
            <div class="form-row">
              <div class="form-group"><label><i class="bi bi-calendar3" aria-hidden="true"></i> Fecha *</label><input type="text" [(ngModel)]="editForm.reservation_date" name="editDate" class="form-control" required appDatePicker="date" [availableDays]="editAvailableDays" [dayOccupancy]="editDayOccupancyMap" [pickerDisabled]="editScheduleLoading() || !editForm.space_id" (pickerValueChange)="onEditDateChange($event)" placeholder="Seleccionar fecha"><small class="availability-help" *ngIf="!editForm.space_id">Selecciona primero un espacio.</small><small class="availability-help" *ngIf="editScheduleLoading()">Consultando días disponibles…</small></div>
              <div class="form-group"><label>Personas *</label><input placeholder="Ej: 4" type="number" [(ngModel)]="editForm.number_of_people" name="editPeople" min="1" [max]="selectedEditSpace()?.capacity || 100" class="form-control" required></div>
            </div>
            <div class="form-row">
              <div class="form-group"><label><i class="bi bi-clock" aria-hidden="true"></i> Hora de inicio *</label><input type="text" [(ngModel)]="editForm.start_time" name="editStart" class="form-control" required appDatePicker="time" [minTime]="editSelectedOpeningTime" [maxTime]="editSelectedClosingTime" [pickerDisabled]="!editSelectedDaySchedule" (pickerValueChange)="onEditTimeChange()" placeholder="Hora de inicio"></div>
              <div class="form-group"><label><i class="bi bi-clock" aria-hidden="true"></i> Hora de finalización *</label><input type="text" [(ngModel)]="editForm.end_time" [afterTime]="editForm.start_time" name="editEnd" class="form-control" required appDatePicker="time" [minTime]="editSelectedOpeningTime" [maxTime]="editSelectedClosingTime" [pickerDisabled]="!editSelectedDaySchedule" (pickerValueChange)="onEditTimeChange()" placeholder="Hora de fin"></div>
            </div>
            <div class="availability-summary" *ngIf="editForm.reservation_date">
              <ng-container *ngIf="editSelectedDaySchedule; else editClosedDay">
                <i class="bi bi-clock" aria-hidden="true"></i>
                Horario disponible: {{ formatClockTime(editSelectedOpeningTime) }} – {{ formatClockTime(editSelectedClosingTime) }}.
                Los selectores avanzan en intervalos de 30 minutos.
              </ng-container>
              <ng-template #editClosedDay>
                <i class="bi bi-calendar-x" aria-hidden="true"></i>
                El espacio no está disponible en el día seleccionado.
              </ng-template>
            </div>
            <div class="form-row">
              <div class="form-group"><label>Estado</label><select [(ngModel)]="editForm.status" name="editStatus" class="form-control"><option value="pending">Pendiente</option><option value="confirmed">Confirmada</option><option value="completed">Completada</option><option value="cancelled">Cancelada</option></select></div>
              <div class="form-group">
                <label>Estado del pago</label>
                <div class="payment-status-locked" [class.paid]="editingReservation()?.payment_status === 'paid'" [class.failed]="editingReservation()?.payment_status === 'failed'">
                  <i class="bi" [class.bi-check-circle]="editingReservation()?.payment_status === 'paid'" [class.bi-x-circle]="editingReservation()?.payment_status === 'failed'" [class.bi-clock-history]="editingReservation()?.payment_status !== 'paid' && editingReservation()?.payment_status !== 'failed'"></i>
                  {{ editingReservation()?.payment_status === 'paid' ? 'Pagada' : (editingReservation()?.payment_status === 'failed' ? (editingReservation()?.payment_failure_reason === 'abandoned' ? 'Pago abandonado' : 'Pago fallido') : 'Pendiente') }}
                </div>
                <small class="availability-help" *ngIf="editingReservation()?.payment_status === 'paid'">Esta reserva ya está pagada. Puedes adjuntar más comprobantes debajo.</small>
                <small class="availability-help" *ngIf="editingReservation()?.payment_status === 'failed'">El cliente inició el pago pero no se completó (rechazado por el banco o abandonado antes de terminar) -- el horario ya quedó libre de nuevo. Si el cliente sí pagó por otro medio, puedes marcarla como pagada abajo.</small>
                <small class="availability-help" *ngIf="editingReservation()?.payment_status !== 'paid' && editingReservation()?.payment_status !== 'failed'">Puedes marcarla como pagada aquí si el cliente ya pagó al negocio por otro medio (efectivo, transferencia, etc.).</small>
                <div *ngIf="editingReservation()?.payment_status !== 'paid'" class="payment-status-choice edit">
                  <label class="radio-pill">
                    <input type="radio" name="editPaymentStatus" value="pending" [(ngModel)]="editForm.payment_status">
                    <span>Pendiente</span>
                  </label>
                  <label class="radio-pill">
                    <input type="radio" name="editPaymentStatus" value="paid" [(ngModel)]="editForm.payment_status">
                    <span><i class="bi bi-check-circle"></i> Marcar como pagada (otro medio)</span>
                  </label>
                </div>
              </div>
            </div>
            <div *ngIf="editForm.payment_status === 'paid' && (editingReservation()?.payment_status !== 'paid' || editingReservation()?.payment_origin === 'external')" class="external-payment-fields">
              <div class="external-payment-header">
                <i class="bi bi-cash-coin" aria-hidden="true"></i>
                <div>
                  <strong>Confirmar pago externo</strong>
                  <span>El cliente pagó al negocio por otro medio (no por la pasarela de TuSala).</span>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Método de pago externo *</label>
                  <select [(ngModel)]="editForm.external_payment_method" name="editExternalMethod" class="form-control" required>
                    <option value="">Selecciona el método</option>
                    <option value="cash">Efectivo</option>
                    <option value="bank_transfer">Transferencia bancaria</option>
                    <option value="cheque">Cheque</option>
                    <option value="card_offline">Tarjeta (fuera de línea)</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
                <div class="form-group">
                  <label><i class="bi bi-calendar3" aria-hidden="true"></i> Fecha del pago</label>
                  <input type="text" [(ngModel)]="editForm.external_paid_at" name="editExternalPaidAt" class="form-control" appDatePicker="date" placeholder="Seleccionar fecha" autocomplete="off">
                </div>
              </div>
              <div class="form-group" *ngIf="editingReservation()?.payment_origin === 'external' && editingReservation()?.receipts?.length">
                <label>Comprobantes ya adjuntados</label>
                <ul class="receipt-list">
                  <li *ngFor="let rc of editingReservation()?.receipts">
                    <a [href]="rc.file_url" target="_blank" rel="noopener noreferrer"><i class="bi bi-paperclip"></i> {{ rc.file_name || 'Ver comprobante' }}</a>
                  </li>
                </ul>
              </div>
              <div class="form-group">
                <label>Comprobante de pago (foto o PDF)</label>
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" (change)="onReceiptFileChange($event, 'edit')" class="form-control">
                <ul *ngIf="editForm.receipts?.length" class="receipt-list">
                  <li *ngFor="let rc of editForm.receipts; let i = index">
                    <i class="bi bi-paperclip"></i> {{ rc.original_name || rc.file_name }}
                    <button type="button" class="receipt-remove" (click)="removeReceipt(i, 'edit')" aria-label="Quitar comprobante">&times;</button>
                  </li>
                </ul>
              </div>
              <div class="form-group">
                <label>Notas (opcional)</label>
                <textarea [(ngModel)]="editForm.external_notes" name="editExternalNotes" class="form-control" rows="2" maxlength="500" placeholder="Observaciones del pago"></textarea>
              </div>
              <div class="external-payment-notice">
                <i class="bi bi-check-circle" aria-hidden="true"></i>
                <span *ngIf="editingReservation()?.payment_status !== 'paid'">Al guardar, la reserva quedará pagada y se enviará al cliente un correo dedicado de <strong>pago confirmado</strong> con el comprobante adjunto. Se cancelará cualquier enlace de pago pendiente.</span>
                <span *ngIf="editingReservation()?.payment_status === 'paid'">Esta reserva ya está pagada -- al guardar solo se actualizan el comprobante y las notas, no se reenvía el correo de pago confirmado.</span>
              </div>
            </div>
            <div class="form-group"><label>Teléfono de contacto</label><input type="tel" [(ngModel)]="editForm.guest_phone" name="editPhone" class="form-control" placeholder="Teléfono del cliente" maxlength="20"></div>
            <ng-container *ngIf="editingReservation()?.is_guest || editingReservation()?.guest_name || editingReservation()?.guest_email">
              <div class="form-row">
                <div class="form-group"><label>Nombre</label><input placeholder="Ej: Ana Pérez" [(ngModel)]="editForm.guest_name" name="editGuestName" class="form-control" maxlength="100"></div>
                <div class="form-group"><label>Correo para notificaciones</label><input placeholder="nombre@correo.com" type="email" [(ngModel)]="editForm.guest_email" name="editGuestEmail" class="form-control" maxlength="100"></div>
              </div>
            </ng-container>
            <div *ngIf="editSpaceExtras().length" class="form-group">
              <label>Extras</label>
              <div class="extras-list">
                <div *ngFor="let extra of editSpaceExtras()" class="extra-item">
                  <label class="extra-checkbox"><input type="checkbox" [checked]="isEditExtraSelected(extra.extra_id!)" (change)="toggleEditExtra(extra.extra_id!, $event)"><span>{{ extra.name }} · {{ '$' + extra.price }}</span></label>
                  <input placeholder="1" *ngIf="isEditExtraSelected(extra.extra_id!)" type="number" min="1" class="extra-quantity" [attr.max]="extra.max_quantity || null" [attr.data-max-message]="extra.max_quantity ? 'Máximo alcanzado: ' + extra.max_quantity + ' por reserva.' : null" [ngModel]="editExtrasMap[extra.extra_id!]" (ngModelChange)="updateEditExtraQuantity(extra.extra_id!, $event)" [name]="'editExtra' + extra.extra_id">
                </div>
              </div>
            </div>
            <div class="form-group"><label>Motivo del cambio *</label><textarea #reasonField [(ngModel)]="editForm.change_reason" name="changeReason" [class.reason-error]="reasonError()" class="form-control" rows="3" required placeholder="Este motivo se registrará en auditoría y se incluirá en la notificación al cliente." maxlength="500" (ngModelChange)="reasonError.set(false)"></textarea></div>
            <div class="edit-notice">Al guardar, la base de datos se actualizará inmediatamente y se enviará un correo al cliente si tiene una dirección registrada.</div>
            <div class="form-actions"><button type="button" class="btn-secondary" (click)="closeEditReservation()">Cancelar</button><button type="submit" class="btn-primary" [disabled]="isSavingReservation()">{{ isSavingReservation() ? 'Guardando…' : 'Guardar y notificar' }}</button></div>
          </form>
        </section>
      </div>

      <!-- Edición de TODO el conjunto de una reserva multi-día -->
      <div *ngIf="editingGroup() as group" class="reservation-modal-overlay" (click)="closeEditGroup()">
        <section class="form-container edit-reservation-modal" (click)="$event.stopPropagation()">
          <div class="form-header">
            <div>
              <small>CONJUNTO · {{ group.dateCount }} fechas ({{ group.dateRangeLabel }})</small>
              <h3>Editar todo el conjunto</h3>
            </div>
            <button type="button" (click)="closeEditGroup()" class="close-btn">×</button>
          </div>
          <form (ngSubmit)="saveGroupReservation()" class="reserva-form" #groupEditFormRef="ngForm" #groupEditFormEl novalidate>
            <div class="edit-notice">Este cambio se aplicará a las {{ group.dateCount }} fechas activas del conjunto a la vez. Para cambiar la fecha o el espacio de una fecha en particular, edítala individualmente desde la lista desplegada.</div>
            <div class="form-row">
              <div class="form-group"><label><i class="bi bi-clock" aria-hidden="true"></i> Hora de inicio *</label><input type="text" [(ngModel)]="groupEditForm.start_time" name="groupEditStart" class="form-control" required appDatePicker="time" placeholder="Hora de inicio"></div>
              <div class="form-group"><label><i class="bi bi-clock" aria-hidden="true"></i> Hora de finalización *</label><input type="text" [(ngModel)]="groupEditForm.end_time" [afterTime]="groupEditForm.start_time" name="groupEditEnd" class="form-control" required appDatePicker="time" placeholder="Hora de fin"></div>
            </div>
            <div class="form-row">
              <div class="form-group"><label>Personas *</label><input placeholder="Ej: 4" type="number" [(ngModel)]="groupEditForm.number_of_people" name="groupEditPeople" min="1" [max]="selectedGroupEditSpace()?.capacity || 100" class="form-control" required></div>
              <div class="form-group"><label>Estado</label><select [(ngModel)]="groupEditForm.status" name="groupEditStatus" class="form-control"><option value="pending">Pendiente</option><option value="confirmed">Confirmada</option><option value="completed">Completada</option><option value="cancelled">Cancelada</option></select></div>
            </div>
            <div class="manual-pricing-panel">
              <strong><i class="bi bi-sliders"></i> Precio personalizado del conjunto (opcional)</strong>
              <small>Se distribuye equitativamente entre las {{ group.dateCount }} fechas. Vacío conserva la tarifa del espacio.</small>
              <div class="form-row">
                <div class="form-group"><label>Subtotal total del conjunto</label><input type="number" [(ngModel)]="groupEditForm.custom_price_total" name="groupCustomPriceTotal" min="0" step="0.01" class="form-control" placeholder="Usar tarifa del espacio"></div>
              </div>
            </div>
            <div class="form-group">
              <label>Estado del pago</label>
              <div class="payment-status-locked" [class.paid]="group.paymentSummary === 'paid'">
                <i class="bi" [class.bi-check-circle]="group.paymentSummary === 'paid'" [class.bi-clock-history]="group.paymentSummary !== 'paid'"></i>
                {{ group.paymentSummary === 'paid' ? 'Pagado' : (group.paymentSummary === 'mixed' ? 'Pago mixto entre fechas' : 'Pendiente') }}
              </div>
              <small class="availability-help" *ngIf="group.paymentSummary === 'paid'">El conjunto ya está pagado. Si este cambio sube el precio, se generará un enlace para cobrar solo la diferencia.</small>
              <small class="availability-help" *ngIf="group.paymentSummary !== 'paid'">Puedes marcar todo el conjunto como pagado aquí si el cliente ya pagó al negocio por otro medio.</small>
              <div *ngIf="group.paymentSummary !== 'paid'" class="payment-status-choice edit">
                <label class="radio-pill">
                  <input type="radio" name="groupEditPaymentStatus" value="pending" [(ngModel)]="groupEditForm.payment_status">
                  <span>Pendiente</span>
                </label>
                <label class="radio-pill">
                  <input type="radio" name="groupEditPaymentStatus" value="paid" [(ngModel)]="groupEditForm.payment_status">
                  <span><i class="bi bi-check-circle"></i> Marcar todo como pagado (otro medio)</span>
                </label>
              </div>
            </div>
            <div *ngIf="groupEditForm.payment_status === 'paid' && group.paymentSummary !== 'paid'" class="external-payment-fields">
              <div class="external-payment-header">
                <i class="bi bi-cash-coin" aria-hidden="true"></i>
                <div>
                  <strong>Confirmar pago externo de las {{ group.dateCount }} fechas</strong>
                  <span>El cliente pagó al negocio por otro medio (no por la pasarela de TuSala). Se aplicará a todo el conjunto.</span>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Método de pago externo *</label>
                  <select [(ngModel)]="groupEditForm.external_payment_method" name="groupEditExternalMethod" class="form-control" required>
                    <option value="">Selecciona el método</option>
                    <option value="cash">Efectivo</option>
                    <option value="bank_transfer">Transferencia bancaria</option>
                    <option value="cheque">Cheque</option>
                    <option value="card_offline">Tarjeta (fuera de línea)</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
                <div class="form-group">
                  <label><i class="bi bi-calendar3" aria-hidden="true"></i> Fecha del pago</label>
                  <input type="text" [(ngModel)]="groupEditForm.external_paid_at" name="groupEditExternalPaidAt" class="form-control" appDatePicker="date" placeholder="Seleccionar fecha" autocomplete="off">
                </div>
              </div>
              <div class="form-group">
                <label>Comprobante de pago (foto o PDF)</label>
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" (change)="onReceiptFileChange($event, 'groupEdit')" class="form-control">
                <ul *ngIf="groupEditForm.receipts?.length" class="receipt-list">
                  <li *ngFor="let rc of groupEditForm.receipts; let i = index">
                    <i class="bi bi-paperclip"></i> {{ rc.original_name || rc.file_name }}
                    <button type="button" class="receipt-remove" (click)="removeReceipt(i, 'groupEdit')" aria-label="Quitar comprobante">&times;</button>
                  </li>
                </ul>
              </div>
              <div class="form-group">
                <label>Notas (opcional)</label>
                <textarea [(ngModel)]="groupEditForm.external_notes" name="groupEditExternalNotes" class="form-control" rows="2" maxlength="500" placeholder="Observaciones del pago"></textarea>
              </div>
              <div class="external-payment-notice">
                <i class="bi bi-check-circle" aria-hidden="true"></i>
                <span>Al guardar, las {{ group.dateCount }} fechas quedarán pagadas y se enviará al cliente un correo dedicado de <strong>pago confirmado</strong> con el comprobante adjunto. Se cancelará cualquier enlace de pago pendiente del conjunto.</span>
              </div>
            </div>
            <div class="form-group"><label>Teléfono de contacto</label><input type="tel" [(ngModel)]="groupEditForm.guest_phone" name="groupEditPhone" class="form-control" placeholder="Teléfono del cliente" maxlength="20"></div>
            <ng-container *ngIf="group.primary.is_guest || group.primary.guest_name || group.primary.guest_email">
              <div class="form-row">
                <div class="form-group"><label>Nombre</label><input placeholder="Ej: Ana Pérez" [(ngModel)]="groupEditForm.guest_name" name="groupEditGuestName" class="form-control" maxlength="100"></div>
                <div class="form-group"><label>Correo para notificaciones</label><input placeholder="nombre@correo.com" type="email" [(ngModel)]="groupEditForm.guest_email" name="groupEditGuestEmail" class="form-control" maxlength="100"></div>
              </div>
            </ng-container>
            <div *ngIf="groupEditSpaceExtras().length" class="form-group">
              <label>Extras</label>
              <div class="extras-list">
                <div *ngFor="let extra of groupEditSpaceExtras()" class="extra-item">
                  <label class="extra-checkbox"><input type="checkbox" [checked]="isGroupEditExtraSelected(extra.extra_id!)" (change)="toggleGroupEditExtra(extra.extra_id!, $event)"><span>{{ extra.name }} · {{ '$' + extra.price }}</span></label>
                  <input placeholder="1" *ngIf="isGroupEditExtraSelected(extra.extra_id!)" type="number" min="1" class="extra-quantity" [attr.max]="extra.max_quantity || null" [attr.data-max-message]="extra.max_quantity ? 'Máximo alcanzado: ' + extra.max_quantity + ' por reserva.' : null" [ngModel]="groupEditExtrasMap[extra.extra_id!]" (ngModelChange)="updateGroupEditExtraQuantity(extra.extra_id!, $event)" [name]="'groupEditExtra' + extra.extra_id">
                </div>
              </div>
            </div>
            <div class="form-group"><label>Motivo del cambio *</label><textarea #groupReasonField [(ngModel)]="groupEditForm.change_reason" name="groupChangeReason" [class.reason-error]="groupReasonError()" class="form-control" rows="3" required placeholder="Este motivo se registrará en auditoría y se incluirá en la notificación al cliente." maxlength="500" (ngModelChange)="groupReasonError.set(false)"></textarea></div>
            <div class="edit-notice">Al guardar, se actualizarán las {{ group.dateCount }} fechas del conjunto y se enviará un correo al cliente si tiene una dirección registrada.</div>
            <div class="form-actions"><button type="button" class="btn-secondary" (click)="closeEditGroup()">Cancelar</button><button type="submit" class="btn-primary" [disabled]="isSavingGroupReservation()">{{ isSavingGroupReservation() ? 'Guardando…' : 'Guardar y notificar' }}</button></div>
          </form>
        </section>
      </div>

      <!-- Modal "ver fechas del conjunto" -- consulta propia y paginada,
           separada de la lista principal (ver openGroupDetail). -->
      <div *ngIf="groupDetailModal() as detail" class="reservation-modal-overlay" (click)="closeGroupDetail()">
        <section class="form-container group-detail-modal" (click)="$event.stopPropagation()">
          <div class="form-header">
            <div>
              <small>CONJUNTO</small>
              <h3>Fechas de {{ detail.spaceTitle }}</h3>
            </div>
            <button type="button" (click)="closeGroupDetail()" class="close-btn">×</button>
          </div>
          <div class="reservas-filter-panel group-detail-filter-panel">
            <div class="reservation-period group-detail-filters">
              <label>Buscar por número de reserva, mes o día
                <input
                  type="text"
                  [ngModel]="groupDetailSearch()"
                  (ngModelChange)="onGroupDetailSearchChange($event)"
                  (keyup.enter)="applyGroupDetailFilters()"
                  placeholder="Ej. R-0001, 12, noviembre…"
                >
              </label>
              <label>Registros
                <select [ngModel]="groupDetailPageSize()" (ngModelChange)="onGroupDetailPageSizeChange($event)">
                  <option *ngFor="let size of groupDetailPageSizeOptions" [ngValue]="size">{{ size }} por página</option>
                </select>
              </label>
              <div class="group-detail-filter-actions">
                <button type="button" class="btn-refresh" (click)="applyGroupDetailFilters()" [disabled]="groupDetailLoading()">Buscar</button>
                <button type="button" class="btn-secondary icon-only" (click)="resetGroupDetailFilters()" [disabled]="groupDetailLoading()" title="Restablecer filtros" aria-label="Restablecer filtros"><i class="bi bi-arrow-counterclockwise"></i></button>
              </div>
            </div>
          </div>
          <div class="group-detail-body">
            <app-ui-skeleton *ngIf="groupDetailLoading()" variant="table" [count]="6" [columnCount]="4" label="Cargando fechas" [compact]="true"></app-ui-skeleton>
            <app-ui-state
              *ngIf="groupDetailReservations().length === 0 && !groupDetailLoading()"
              tone="empty"
              icon="bi bi-calendar-week"
              title="No encontramos fechas"
              message="Prueba con otro número de reserva, mes o día."
            ></app-ui-state>
            <div *ngIf="groupDetailReservations().length > 0 && !groupDetailLoading()" class="reservation-directory">
              <ng-container *ngFor="let fecha of groupDetailReservations(); trackBy: trackByReservationId">
                <ng-container *ngTemplateOutlet="reservationRow; context: { $implicit: fecha }"></ng-container>
              </ng-container>
            </div>
          </div>
          <app-pagination [page]="groupDetailPage()" [pages]="groupDetailPages()" [total]="groupDetailTotal()" [limit]="groupDetailPageSize()" [disabled]="groupDetailLoading()" itemLabel="fechas" ariaLabel="Paginación de fechas del conjunto" (pageChange)="goToGroupDetailPage($event)"></app-pagination>
        </section>
      </div>

      <!-- Enviar/copiar enlace de pago -- modal propio (no el genérico de
           DialogService) para poder ofrecer, además de "Enviar enlace" por
           correo, un botón de "Copiar enlace" como respaldo si el cliente
           tiene problemas para recibir el correo (para compartir por
           WhatsApp u otra red directamente). -->
      <div *ngIf="sendLinkModal() as linkModal" class="reservation-modal-overlay" (click)="closeSendPaymentLinkModal()">
        <section class="form-container send-link-modal" (click)="$event.stopPropagation()">
          <div class="form-header">
            <div>
              <small>ENLACE DE PAGO</small>
              <h3>{{ linkModal.title }}</h3>
            </div>
            <button type="button" (click)="closeSendPaymentLinkModal()" class="close-btn" [disabled]="sendLinkBusy()">×</button>
          </div>
          <p class="send-link-message"><i class="bi bi-envelope-paper"></i><span>{{ linkModal.message }}</span></p>
          <div class="send-link-actions">
            <button type="button" class="btn-secondary" (click)="closeSendPaymentLinkModal()" [disabled]="sendLinkBusy()">Cancelar</button>
            <div class="send-link-actions-primary">
              <button type="button" class="btn-copy-link" (click)="copyPaymentLink()" [disabled]="sendLinkBusy()">
                <i class="bi" [class.bi-clipboard]="!linkCopied()" [class.bi-clipboard-check]="linkCopied()"></i>
                {{ linkCopied() ? '¡Copiado!' : 'Copiar enlace' }}
              </button>
              <button type="button" class="btn-primary" (click)="confirmSendPaymentLink()" [disabled]="sendLinkBusy()">
                <span class="refresh-spinner" *ngIf="sendLinkBusy()" aria-hidden="true"></span>
                <i class="bi bi-send" *ngIf="!sendLinkBusy()"></i>
                {{ sendLinkBusy() ? 'Enviando…' : 'Enviar enlace' }}
              </button>
            </div>
          </div>
        </section>
      </div>

      <!-- Lista de reservaciones -->
      <div class="table-container">
        <app-ui-skeleton
          *ngIf="isLoading()"
          class="reservations-loading"
          variant="table"
          [count]="6"
          [columnCount]="4"
          label="Cargando reservas"
          [compact]="true"
        ></app-ui-skeleton>

        <app-ui-state
          *ngIf="reservaciones().length === 0 && !isLoading()"
          tone="empty"
          [icon]="searchTerm() ? '' : 'bi bi-calendar-week'"
          [title]="searchTerm() ? 'No encontramos reservaciones' : 'Aún no hay reservaciones'"
          [message]="searchTerm() ? 'Prueba con otro nombre, correo o referencia.' : 'No hay reservaciones registradas.'"
        ></app-ui-state>
        
        <div *ngIf="reservaciones().length > 0 && !isLoading()" class="reservation-directory">
          <ng-container *ngFor="let card of groupedReservaciones(); trackBy: trackByCard">
            <ng-container *ngIf="card.type === 'single'">
              <ng-container *ngTemplateOutlet="reservationRow; context: { $implicit: card.reservation }"></ng-container>
            </ng-container>
            <ng-container *ngIf="card.type === 'group'">
              <app-management-record-card [clickable]="false" class="reservation-group-card" kind="operation" [status]="reservationTone(card.primary.status)">
                <span recordIcon><i class="bi bi-calendar-week"></i></span>
                <div recordPrimary>
                  <small>CONJUNTO · {{ card.summary.date_count }} fechas</small>
                  <strong>{{ getReservationSpaceTitle(card.primary) }}</strong>
                  <span>{{ formatDate(card.summary.start_date) }} – {{ formatDate(card.summary.end_date) }} · {{ formatClockTime(card.primary.start_time) }}–{{ formatClockTime(card.primary.end_time) }} · {{ card.primary.number_of_people }} pers.</span>
                </div>
                <div recordSummary>
                  <small>CLIENTE</small>
                  <strong>{{ getReservationCustomerName(card.primary) }}</strong>
                  <span>{{ getReservationCustomerEmail(card.primary) }} · {{ getReservationCustomerPhone(card.primary) }}</span>
                </div>
                <div recordStatus>
                  <small>TOTAL DEL CONJUNTO</small>
                  <strong>{{ '$' + card.summary.total.toFixed(2) }}</strong>
                  <span *ngIf="card.summary.payment_summary !== 'paid'" class="pending-total">Pendiente: {{ '$' + card.summary.pending_total.toFixed(2) }}</span>
                  <span class="pricing-detail" *ngIf="card.primary.custom_price_base != null" title="El admin fijó el precio manualmente para todo el conjunto">
                    <i class="bi bi-pencil-square"></i> Precio personalizado por fecha
                  </span>
                  <span class="pricing-detail discount" *ngIf="card.primary.custom_price_base == null && card.primary.discount_amount && +card.primary.discount_amount > 0" title="Descuento por tarifa escalonada del espacio">
                    <i class="bi bi-tag"></i> {{ card.primary.tier_range ? card.primary.tier_range + ' · ' : '' }}descuento por fecha
                  </span>
                  <span class="reservation-badges">
                    <span *ngFor="let s of card.summary.status_counts; trackBy: trackByStatus" class="status-tag" [class]="'status-' + s.status.toLowerCase()">{{ s.count }} {{ getEstadoText(s.status) }}</span>
                    <span class="payment-tag" [class.paid]="card.summary.payment_summary === 'paid'">
                      <i class="bi" [class.bi-check-circle]="card.summary.payment_summary === 'paid'" [class.bi-clock]="card.summary.payment_summary !== 'paid'"></i>
                      {{ card.summary.payment_summary === 'paid' ? 'Pagado' : (card.summary.payment_summary === 'mixed' ? 'Pago mixto' : 'Pendiente') }}
                    </span>
                  </span>
                </div>
                <div recordActions>
                  <button type="button" class="icon-action" title="Editar todo el conjunto" aria-label="Editar todo el conjunto" (click)="openEditGroup(card)"><i class="bi bi-pencil-square"></i></button>
                  <button *ngIf="card.summary.payment_summary !== 'paid'" type="button" class="icon-action" title="Enviar enlace de pago del conjunto" aria-label="Enviar enlace de pago del conjunto" [disabled]="sendingGroupLinkId() === card.groupId" (click)="sendGroupPaymentLink(card)"><i class="bi" [class.bi-send]="sendingGroupLinkId() !== card.groupId" [class.bi-arrow-clockwise]="sendingGroupLinkId() === card.groupId" [class.spin]="sendingGroupLinkId() === card.groupId"></i></button>
                  <button type="button" class="icon-action danger" title="Enviar todo el conjunto a la Papelera" aria-label="Enviar todo el conjunto a la Papelera" [disabled]="cancellingGroupId() === card.groupId" (click)="deleteReservationGroup(card)"><i class="bi" [class.bi-trash3]="cancellingGroupId() !== card.groupId" [class.bi-arrow-clockwise]="cancellingGroupId() === card.groupId" [class.spin]="cancellingGroupId() === card.groupId"></i></button>
                  <button type="button" class="icon-action" title="Ver fechas del conjunto" aria-label="Ver fechas del conjunto" (click)="openGroupDetail(card)"><i class="bi bi-eye"></i></button>
                </div>
              </app-management-record-card>
            </ng-container>
          </ng-container>
        </div>

        <ng-template #reservationRow let-reserva>
          <app-management-record-card [clickable]="false" kind="operation" [status]="reservationTone(reserva.status)">
            <span recordIcon><i class="bi bi-calendar-check"></i></span>
            <div recordPrimary>
              <small>RESERVA {{ getReservationReference(reserva) }}</small>
              <strong>{{ getReservationSpaceTitle(reserva) }}</strong>
              <span>{{ formatDate(reserva.date || reserva.reservation_date) }} · {{ formatClockTime(reserva.start_time) }}–{{ formatClockTime(reserva.end_time) }} · {{ reserva.number_of_people }} pers.</span>
            </div>
            <div recordSummary>
              <small>CLIENTE</small>
              <strong>{{ getReservationCustomerName(reserva) }}</strong>
              <span>{{ getReservationCustomerEmail(reserva) }} · {{ getReservationCustomerPhone(reserva) }}</span>
            </div>
            <div recordStatus>
              <small>TOTAL</small>
              <strong>{{ '$' + reserva.total_price }}</strong>
              <span class="pricing-detail" *ngIf="reserva.custom_price_base != null" title="El admin fijó este precio manualmente en vez de usar la tarifa del espacio">
                <i class="bi bi-pencil-square"></i> Precio personalizado (base {{ '$' + reserva.custom_price_base }})
              </span>
              <span class="pricing-detail discount" *ngIf="reserva.custom_price_base == null && reserva.discount_amount && +reserva.discount_amount > 0" title="Descuento por tarifa escalonada del espacio">
                <i class="bi bi-tag"></i> {{ reserva.tier_range ? reserva.tier_range + ' · ' : '' }}antes {{ '$' + reserva.original_price }} · ahorro {{ '$' + reserva.discount_amount }}
              </span>
              <span class="reservation-badges">
                <span class="status-tag" [class]="'status-' + reserva.status.toLowerCase()">{{ getEstadoText(reserva.status) }}</span>
                <span class="payment-tag" [class.paid]="reserva.payment_status === 'paid'" [class.failed]="reserva.payment_status === 'failed'">
                  <i class="bi" [class.bi-check-circle]="reserva.payment_status === 'paid'" [class.bi-x-circle]="reserva.payment_status === 'failed'" [class.bi-clock]="reserva.payment_status !== 'paid' && reserva.payment_status !== 'failed'"></i>
                  {{ reserva.payment_status === 'paid' ? 'Pagado' : (reserva.payment_status === 'failed' ? (reserva.payment_failure_reason === 'abandoned' ? 'Pago abandonado' : 'Pago fallido') : 'Pendiente') }}
                </span>
                <span *ngIf="reserva.payment_status === 'paid' && reserva.payment_origin === 'external'" class="origin-tag" title="El cliente pagó al negocio por otro medio">
                  <i class="bi bi-bank"></i> Externo
                </span>
                <ng-container *ngIf="reserva.receipts?.length">
                  <a *ngFor="let rc of reserva.receipts" class="receipt-link" [href]="rc.file_url" target="_blank" rel="noopener noreferrer" title="Ver comprobante">
                    <i class="bi bi-paperclip"></i> Comprobante
                  </a>
                </ng-container>
              </span>
            </div>
            <div recordActions>
              <button type="button" class="icon-action" title="Editar reserva" aria-label="Editar reserva" (click)="openEditReservation(reserva)"><i class="bi bi-pencil"></i></button>
              <button *ngIf="reserva.payment_status !== 'paid'" type="button" class="icon-action" [title]="reserva.status === 'cancelled' ? 'Reactivar y enviar enlace de pago (revalida que el horario siga libre)' : 'Enviar enlace de pago'" [attr.aria-label]="reserva.status === 'cancelled' ? 'Reactivar y enviar enlace de pago' : 'Enviar enlace de pago'" (click)="sendPaymentLink(reserva)"><i class="bi" [class.bi-send]="reserva.status !== 'cancelled'" [class.bi-arrow-repeat]="reserva.status === 'cancelled'"></i></button>
              <button type="button" class="icon-action danger" title="Enviar a la Papelera" aria-label="Enviar a la Papelera" [disabled]="cancellingId() === reserva.reservation_id" (click)="deleteReservation(reserva)" cooldownBubble data-cooldown-key="delete-reservation"><i class="bi" [class.bi-trash3]="cancellingId() !== reserva.reservation_id" [class.bi-arrow-clockwise]="cancellingId() === reserva.reservation_id" [class.spin]="cancellingId() === reserva.reservation_id"></i></button>
            </div>
          </app-management-record-card>
        </ng-template>
      </div>
      <app-pagination [page]="reservationsPage()" [pages]="reservationsPages()" [total]="reservationsTotal()" [limit]="reservationsPageSize()" [disabled]="isLoading()" itemLabel="reservas" ariaLabel="Paginación de reservas" (pageChange)="goToReservationsPage($event)"></app-pagination>
    </div>

  `,
  styles: [`
    .reservas-container {
      width: 100%;
    }
    
    .reservas-topbar {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 12px;
      margin-bottom: 14px;
    }
    .reservas-filter-panel {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 30px;
    }
    
    .btn-primary {
      background: var(--ts-bg);
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;
    }
    
    .btn-primary:hover:not(:disabled) {
      background: var(--ts-bg-gray-800);
    }

    .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .form-container {
      width: min(820px, 100%);
      max-height: 92vh;
      overflow-y: auto;
      box-sizing: border-box;
      background: white;
      border-radius: 12px;
      padding: 32px;
      margin-bottom: 30px;
      box-shadow: 0 2px 8px var(--ts-shadow-gray-1000-a10);
    }

    .form-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    .form-header h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 32px;
      color: var(--ts-text-gray-400);
      cursor: pointer;
      line-height: 1;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
    }

    .close-btn:hover {
      color: var(--ts-text-gray-800);
    }
    
    .reserva-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    
    .form-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 16px;
    }
    
    .form-group {
      display: flex;
      flex-direction: column;
    }
    
    .form-group label {
      margin-bottom: 8px;
      font-weight: 600;
      color: var(--ts-text-gray-800);
      font-size: 14px;
    }
    
    .form-control {
      padding: 10px 14px;
      border: 2px solid var(--ts-border-gray-100);
      border-radius: 6px;
      font-size: 14px;
      transition: all 0.3s ease;
    }
    
    .form-control:focus {
      outline: none;
      border-color: var(--ts-bg);
    }

    .radio-group {
      display: flex;
      gap: 24px;
    }

    .radio-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-weight: 500;
    }

    .radio-label input[type="radio"] {
      cursor: pointer;
    }

    .extras-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px;
      background: var(--ts-bg-gray-0-2);
      border-radius: 8px;
    }

    .extra-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
    }

    .extra-checkbox {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      flex: 1;
    }

    .extra-quantity {
      width: 80px;
      padding: 6px 10px;
      border: 2px solid var(--ts-border-gray-100);
      border-radius: 6px;
      font-size: 14px;
    }
    
    .form-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 8px;
      padding-top: 24px;
      border-top: 2px solid var(--ts-border-gray-100);
    }
    
    .btn-secondary {
      background: white;
      color: var(--ts-text-gray-800);
      border: 2px solid var(--ts-border-gray-100);
      padding: 12px 24px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;
    }
    
    .btn-secondary:hover {
      border-color: var(--ts-border-gray-400);
      background: var(--ts-bg-gray-0-2);
    }
    
    .table-container {
      background: white;
      border-radius: 12px;
      overflow-x: auto;
      overflow-y: visible;
      box-shadow: 0 2px 8px var(--ts-shadow-gray-1000-a10);
    }
    
    
    .reservas-table {
      width: 100%;
      border-collapse: collapse;
      min-width: 1200px;
    }
    
    .reservas-table th {
      background: var(--ts-bg-gray-50);
      padding: 15px 12px;
      text-align: left;
      font-weight: 600;
      color: var(--ts-text-gray-800);
      border-bottom: 2px solid var(--ts-border-gray-100);
      white-space: nowrap;
      font-size: 13px;
    }
    
    .reservas-table td {
      padding: 15px 12px;
      border-bottom: 1px solid var(--ts-border-gray-100);
      font-size: 13px;
    }
    
    .reservas-table tr:hover {
      background: var(--ts-bg-gray-0-2);
    }

    .reservas-table th:first-child,
    .reservas-table td:first-child {
      width: 80px;
      text-align: center;
    }
    
    .status-confirmed,
    .status-confirmada {
      background: var(--ts-bg-green-100);
      color: var(--ts-text-green-800);
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }
    
    .status-pending,
    .status-pendiente {
      background: var(--ts-bg-amber-100);
      color: var(--ts-text-amber-750);
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }
    
    .status-cancelled,
    .status-cancelada {
      background: var(--ts-bg-red-100);
      color: var(--ts-text-red-700);
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }
    
    .reservation-modal-overlay {
      position: fixed;
      inset: 0;
      z-index: 3000;
      display: grid;
      place-items: center;
      padding: 16px;
      background: var(--ts-bg-navy-900-a58);
      backdrop-filter: blur(2px);
    }
    /* El modal de editar UNA fecha puede abrirse desde DENTRO del modal de
       fechas del conjunto (mismo z-index base) -- sin esto, por orden de
       aparición en el DOM (el de fechas del conjunto está más abajo en la
       plantilla), el de edición individual quedaba pintado detrás en vez de
       encima. Necesita !important: styles.css (global, fuera de este
       componente) fuerza z-index:2000 !important sobre TODOS los
       ".reservation-modal-overlay" del panel -- sin !important aquí, esa
       regla global lo aplastaba y el z-index de este archivo no hacía nada.
       La especificidad (2 clases + el atributo de encapsulación de Angular)
       ya es mayor que la del selector global, así que gana sin afectar a
       ningún otro modal de la aplicación. */
    .reservation-modal-overlay.reservation-modal-overlay--elevated {
      z-index: 3100 !important;
    }

    .reservas-container { color: var(--ts-text-navy-800-3); }
    .reservas-filter-panel { justify-content: flex-end; margin-bottom: 1.15rem; }
    .table-container { overflow: visible; background: transparent !important; box-shadow: none !important; border-radius: 0 !important; padding: 0 !important; border: none !important; }
    .reservation-directory { display: grid; gap: .7rem; }
    .reservation-badges { display: flex; flex-wrap: wrap; align-items: center; gap: .35rem; }
    .pricing-detail { display: flex; align-items: center; gap: .3rem; margin-top: .15rem; color: var(--ts-text-sky-300-21); font-size: .68rem; font-weight: 650; }
    .pricing-detail i { font-size: .7rem; }
    .pricing-detail.discount { color: var(--ts-text-green-300-7); }
    .reservation-group-card { border-left: 3px solid var(--ts-bg); }
    .pending-total { display: inline-block; margin-top: .1rem; color: var(--ts-warning-text); font-size: .74rem; font-weight: 650; }
    .reservation-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(310px, 1fr)); gap: 1rem; }
    .reservation-card { overflow: hidden; border: 1px solid var(--ts-border-sky-100-7); border-radius: 15px; background: var(--ts-text); box-shadow: 0 7px 18px var(--ts-shadow-navy-800-a6); }
    .reservation-card-header { display: flex; align-items: flex-start; gap: .65rem; padding: 1rem 1rem .85rem; border-bottom: 1px solid var(--ts-border-sky-50-3); background: linear-gradient(135deg, var(--ts-bg-sky-0-3), var(--ts-bg-blue-50-8)); }
    .reservation-card-icon { display: grid; place-items: center; width: 34px; height: 34px; flex: 0 0 34px; border-radius: 9px; color: var(--ts-text-blue-550-6); background: var(--ts-bg-blue-550-a10-2); border: 1px solid var(--ts-border-blue-550-a24); font-size: .95rem; }
    .reservation-card-heading { flex: 1; min-width: 0; }
    .reservation-card-tags { display: flex; flex-direction: column; align-items: flex-end; gap: .3rem; flex: 0 0 auto; }
    .reservation-id, .reservation-details span { display: block; color: var(--ts-text-blue-500-6); font-size: .69rem; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; }
    .reservation-card h3 { margin: .25rem 0 0; color: var(--ts-text-navy-750); font-size: 1rem; overflow-wrap: anywhere; }
    .status-tag { padding: .18rem .55rem; border-radius: 999px; font-size: .64rem; font-weight: 700; white-space: nowrap; }
    .status-tag.status-completed, .status-tag.status-completada { background: var(--ts-bg-blue-50-9); color: var(--ts-text-blue-600-3); }
    .payment-tag { display: inline-flex; align-items: center; gap: .25rem; padding: .16rem .5rem; border-radius: 999px; font-size: .62rem; font-weight: 650; white-space: nowrap; color: var(--ts-text-orange-650); background: var(--ts-bg-amber-100-2); }
    .payment-tag.paid { color: var(--ts-text-teal-700); background: var(--ts-bg-green-100-2); }
    .payment-tag.failed { color: var(--ts-text-red-600-3); background: var(--ts-bg-red-50-5); }
    .origin-tag { display: inline-flex; align-items: center; gap: .25rem; padding: .16rem .5rem; border-radius: 999px; font-size: .62rem; font-weight: 700; white-space: nowrap; color: var(--ts-text-blue-600-4); background: var(--ts-bg-blue-50-10); }
    .receipt-link { display: inline-flex; align-items: center; gap: .25rem; padding: .16rem .5rem; border-radius: 999px; font-size: .62rem; font-weight: 700; white-space: nowrap; color: var(--ts-text-blue-550-6); background: var(--ts-bg-blue-50-7); text-decoration: none; }
    .receipt-link:hover { text-decoration: underline; }
    .payment-status-locked { display: inline-flex; align-items: center; gap: .4rem; padding: .55rem .8rem; border-radius: 9px; border: 1px solid var(--ts-border-amber-200); font-size: .82rem; font-weight: 700; color: var(--ts-text-orange-650); background: var(--ts-bg-amber-50); }
    .payment-status-locked.paid { color: var(--ts-text-teal-700); border-color: var(--ts-border-green-200); background: var(--ts-bg-green-50-2); }
    .payment-status-locked.failed { color: var(--ts-text-red-600-3); border-color: var(--ts-border-red-150-2); background: var(--ts-bg-red-50-6); }
    .payment-status-choice { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: .35rem; }
    .payment-status-choice .radio-pill { display: inline-flex; align-items: center; gap: .4rem; padding: .5rem .75rem; border: 1px solid var(--ts-border-sky-100-8); border-radius: 9px; cursor: pointer; font-size: .8rem; color: var(--ts-text-navy-700); background: var(--ts-bg-gray-0); transition: .2s; }
    .payment-status-choice .radio-pill:hover { border-color: var(--ts-border-blue-250); }
    .payment-status-choice .radio-pill input { margin: 0; accent-color: var(--ts-accent-blue-550); }
    .payment-status-choice .radio-pill:has(input:checked) { border-color: var(--ts-border-blue-550); background: var(--ts-bg-blue-50-11); color: var(--ts-text-blue-650); font-weight: 650; }
    .payment-status-choice.edit { margin-top: .5rem; }
    .external-payment-fields { margin-top: .9rem; padding: 1rem 1rem 1.1rem; border: 1px solid var(--ts-border-green-500-a50); border-radius: 12px; background: var(--ts-bg-green-500-a13); }
    .external-payment-header { display: flex; align-items: flex-start; gap: .6rem; margin: 0 0 .9rem; padding-bottom: .8rem; border-bottom: 1px solid var(--ts-border-green-500-a22); }
    .external-payment-header i { flex: 0 0 auto; margin-top: .1rem; font-size: 1.05rem; color: var(--ts-success-text); }
    .external-payment-header strong { display: block; color: var(--ts-text-green-150-2); font-size: .92rem; }
    .external-payment-header span { display: block; margin-top: .1rem; color: var(--ts-text-green-300-8); font-size: .78rem; line-height: 1.4; }
    .external-payment-fields .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: .8rem; }
    .external-payment-notice { display: flex; align-items: flex-start; gap: .5rem; margin-top: .9rem; padding-top: .8rem; border-top: 1px solid var(--ts-border-green-500-a22); color: var(--ts-text-green-300-8); font-size: .78rem; line-height: 1.5; }
    .external-payment-notice i { flex: 0 0 auto; margin-top: .15rem; color: var(--ts-success-text); }
    @media (max-width: 640px) { .external-payment-fields .form-row { grid-template-columns: 1fr; } }
    .receipt-list { list-style: none; margin: .5rem 0 0; padding: 0; }
    .receipt-list li { display: flex; align-items: center; gap: .4rem; padding: .35rem .5rem; margin-bottom: .3rem; border: 1px solid var(--ts-border-sky-100-9); border-radius: 7px; background: var(--ts-bg-gray-0); font-size: .78rem; color: var(--ts-text-navy-750-3); }
    .receipt-list .receipt-remove { margin-left: auto; border: 0; background: transparent; color: var(--ts-text-red-550-4); font-size: 1rem; line-height: 1; cursor: pointer; }
    .reservation-details { display: grid; grid-template-columns: 1fr 1fr; gap: .8rem; padding: 1rem; }
    .reservation-details strong { display: block; margin-top: .22rem; color: var(--ts-text-navy-700-2); font-size: .85rem; overflow-wrap: anywhere; }
    .reservation-details .reservation-total { color: var(--ts-text-blue-550-6); font-size: 1rem; }
    .reservation-period{display:flex;flex-wrap:wrap;align-items:end;gap:.65rem;width:100%}
    .reservation-period label{display:grid;flex:1 1 140px;gap:.25rem;color:var(--ts-text-blue-500-7);font-size:.72rem;font-weight:750;min-width:0}
    .reservation-period label:first-child{flex:2.2 1 220px}
    .reservation-period select,.reservation-period input{min-height:38px;padding:.45rem .6rem;border:1px solid var(--ts-border-sky-100-8);border-radius:8px;background:var(--ts-text);color:var(--ts-text-navy-750-3);width:100%;box-sizing:border-box}
    .reservation-period .btn-refresh{flex:0 0 auto;margin-left:auto}
    .btn-refresh{min-height:38px;padding:.45rem .85rem;border:1px solid var(--ts-border-blue-150-2);border-radius:8px;color:var(--ts-text-blue-550-7);background:var(--ts-bg-blue-50-11);font-weight:750;cursor:pointer;white-space:nowrap}
    .reservation-actions{display:flex;justify-content:flex-end;gap:.5rem;padding:.7rem 1rem;border-top:1px solid var(--ts-border-sky-50-3);background:var(--ts-bg-sky-0-3)}
    .icon-btn{display:grid;place-items:center;width:36px;height:36px;border-radius:8px;font-size:.85rem;cursor:pointer;border:1px solid var(--ts-border-blue-200-2);color:var(--ts-text-sky-600-5);background:var(--ts-text)}.icon-btn:hover{background:var(--ts-bg-blue-50-12)}
    .icon-btn.danger{border-color:var(--ts-border-red-150-3);color:var(--ts-text-red-600-4);background:var(--ts-bg-red-0-2)}.icon-btn.danger:hover{background:var(--ts-bg-red-50-7)}
    .edit-reservation-modal{width:min(850px,100%);padding:0;border:1px solid var(--ts-border-strong);border-radius:18px;box-shadow:0 30px 80px var(--ts-shadow-gray-1000-a48);overflow-y:hidden}.form-container.edit-reservation-modal .form-header{position:relative;padding:1.1rem 1.35rem;margin:0;color:var(--ts-text);background:var(--ts-bg-navy-950);border-bottom:2px solid var(--ts-border-navy-750);z-index:3}.form-container.edit-reservation-modal .form-header small{color:var(--ts-text-blue-250);font-size:.7rem;font-weight:800;letter-spacing:.08em}.form-container.edit-reservation-modal .form-header h3{margin:.2rem 0 0}.form-container.edit-reservation-modal .close-btn{color:var(--ts-text)}.edit-reservation-modal .reserva-form{padding:1.1rem 1.35rem 0;max-height:calc(92vh - 150px);overflow-y:auto}.edit-reservation-modal textarea{resize:vertical}.edit-notice{padding:.75rem .85rem;border-left:4px solid var(--ts-border-sky-500-3);border-radius:8px;color:var(--ts-text-sky-150);background:var(--ts-bg-navy-850-2);font-size:.8rem;line-height:1.5}
    /* Barra y modales integrados al fondo del módulo. */
    .reservas-container{color:var(--ts-text);background:transparent}
    /* Panel de búsqueda/filtros como una sola tarjeta cohesiva, separada de
       las pestañas de estado que van debajo — antes ambas quedaban "sueltas"
       una sobre la otra sin agrupación visual, dando sensación de dos barras
       de filtro compitiendo por espacio. */
    .reservas-filter-panel{align-items:flex-end;gap:.75rem;margin:0 0 1rem;padding:.9rem 1rem;background:var(--ts-bg-elevated);border:1px solid var(--ts-border-strong);border-radius:14px;flex-wrap:wrap}
    .reservas-status-tabs{display:block;margin-bottom:1.15rem}
    .reservation-period label{color:var(--ts-text-blue-300)}
    .reservation-period select,.reservation-period input{height:40px;min-height:40px;color:var(--ts-text-on-accent-16);background:var(--ts-scrollbar-track);border-color:var(--ts-border-strong)}
    .btn-refresh,.reservas-filter-panel>.btn-primary{display:inline-flex;align-items:center;justify-content:center;gap:.45rem;height:40px;min-height:40px;margin:0;padding:0 .9rem}
    .btn-refresh{color:var(--ts-text-sky-150);background:var(--ts-bg-elevated);border-color:var(--ts-divider)}.btn-refresh:disabled{cursor:wait;opacity:.82}
    .refresh-spinner{width:14px;height:14px;border:2px solid var(--ts-border-gray-0-a28);border-top-color:var(--ts-text);border-radius:50%;animation:spin .7s linear infinite}
    @keyframes spin{to{transform:rotate(360deg)}}
    .form-container{color:var(--ts-text);background:var(--ts-bg-elevated);border:1px solid var(--ts-border-strong);box-shadow:0 30px 80px var(--ts-shadow-gray-1000-a42)}
    .form-container.create-reservation-modal.create-reservation-modal{width:min(920px,100%)!important;padding:0;overflow:hidden}
    .form-header,.edit-reservation-modal .form-header{position:sticky;top:0;z-index:3;margin:0;padding:1.1rem 1.35rem;color:var(--ts-text);background:var(--ts-bg-navy-950);border-bottom:2px solid var(--ts-border-navy-750);box-shadow:0 2px 12px var(--ts-shadow-gray-1000-a35)}
    .create-reservation-modal>.form-group{margin:0;padding:1.25rem 1.35rem 0}
    .create-reservation-modal>.reserva-form{max-height:calc(92vh - 150px);overflow-y:auto;padding:1.1rem 1.35rem 0}
    .form-group label,.radio-label{color:var(--ts-text-sky-150-4)}
    .form-group>label i{margin-right:.35rem;color:var(--ts-text-blue-250)}
    .form-control,.extra-quantity{color:var(--ts-text-soft-2);background:var(--ts-scrollbar-track);border-color:var(--ts-divider)}
    .availability-help{display:block;margin-top:.4rem;color:var(--ts-text-sky-350-28);font-size:.76rem}
    .availability-summary{display:flex;align-items:center;gap:.55rem;margin:.15rem 0 1rem;padding:.75rem .85rem;color:var(--ts-text-sky-150);background:var(--ts-bg-navy-850-2);border:1px solid var(--ts-divider);border-radius:10px;font-size:.82rem}
    .availability-summary.availability-error{color:var(--ts-text-red-150-10);background:var(--ts-bg-red-350-a16);border-color:var(--ts-border-red-350-a50)}
    .availability-summary.total-price-preview{color:var(--ts-text-green-300-7);background:var(--ts-bg-green-600-a10);border-color:var(--ts-border-green-450-a28)}
    .price-preview-tag{display:inline-flex;align-items:center;gap:.3rem;margin-left:.6rem;padding:.2rem .55rem;border-radius:999px;background:var(--ts-bg-green-600-a22);color:var(--ts-text-green-300-7);font-size:.72rem;font-weight:700}
    .manual-pricing-panel{margin:1rem 0;padding:.85rem 1rem;border:1px solid var(--ts-border-sky-300-a28);border-radius:10px;background:var(--ts-bg-navy-800-a45)}
    .manual-pricing-panel>strong{display:block;color:var(--ts-text-sky-100-8);margin-bottom:.25rem}
    .manual-pricing-panel>small{display:block;color:var(--ts-text-sky-300-21);line-height:1.45;margin-bottom:.65rem}
    .multi-date-toggle-row{grid-column:1/-1;margin-bottom:.4rem}
    .multi-date-toggle{display:flex;align-items:center;gap:.55rem;padding:.6rem .8rem;color:var(--ts-text-sky-150);background:var(--ts-bg-navy-850-2);border:1px solid var(--ts-divider);border-radius:9px;font-size:.82rem;font-weight:650;cursor:pointer}
    .multi-date-toggle input{width:16px;height:16px;accent-color:var(--ts-primary)}
    .extras-list,.edit-notice{color:var(--ts-text-sky-150-5);background:var(--ts-bg-navy-850-2);border-color:var(--ts-divider)}
    .form-actions{position:sticky;bottom:0;z-index:3;margin:1.25rem -1.35rem 0;padding:1rem 1.35rem;border-top:2px solid var(--ts-border-navy-750);background:var(--ts-bg-navy-950);box-shadow:0 -10px 22px var(--ts-shadow-gray-1000-a35)}
    .form-actions .btn-secondary{color:var(--ts-text-sky-100);background:var(--ts-bg-elevated);border-color:var(--ts-border-navy-650)}
    .reservations-loading{display:block;min-height:220px}
@media(max-width:900px){.reservas-topbar{justify-content:flex-end}.reservas-topbar .btn-primary{width:100%}.reservas-filter-panel{display:grid;grid-template-columns:1fr;align-items:stretch;gap:.7rem}.reservation-period label{flex-basis:45%}.reservation-period label:first-child{flex-basis:100%}}
    @media(max-width:900px){.reservation-cards{grid-template-columns:repeat(2,minmax(0,1fr))}}
    @media(max-width:600px){.reservation-period label{flex-basis:100%}.reservation-period .btn-refresh{width:100%;justify-content:center;margin-left:0}.reservation-cards{grid-template-columns:1fr}.reservation-card{font-size:.8rem}.reservation-card-header{padding:.8rem .75rem .7rem}.reservation-card-header h3{font-size:.92rem}.reservation-details{padding:.85rem}.reservation-details strong{font-size:.82rem}.reservation-actions{padding:.55rem .85rem}.icon-btn{width:34px;height:34px}}
    .reservation-card{background:var(--ts-bg-elevated) !important;border-color:var(--ts-border-strong) !important;box-shadow:0 7px 18px var(--ts-shadow-gray-1000-a18) !important}
    .reservation-card-header{background:linear-gradient(135deg,var(--ts-bg-navy-900-16),var(--ts-bg-navy-850-2));border-bottom-color:var(--ts-border-strong)}
    .reservation-card-icon{color:var(--ts-text-blue-250);background:var(--ts-bg-blue-400-a22);border-color:var(--ts-border-blue-400-a50)}
    .reservation-card h3{color:var(--ts-text)}
    .reservation-details strong{color:var(--ts-text-sky-150-4)}
    .reservation-details .reservation-total{color:var(--ts-text-blue-250)}
    .status-tag.status-completed,.status-tag.status-completada{background:var(--ts-bg-blue-250-a16);color:var(--ts-text-blue-150-6)}
    .status-confirmed,.status-confirmada{background:var(--ts-bg-green-500-a22);color:var(--ts-success-text)}
    .status-pending,.status-pendiente{background:var(--ts-bg-orange-450-a22);color:var(--ts-warning-text)}
    .status-cancelled,.status-cancelada{background:var(--ts-bg-red-350-a22);color:var(--ts-text-red-200-5)}
    .payment-tag{color:var(--ts-warning-text);background:var(--ts-bg-orange-450-a19)}
    .payment-tag.paid{color:var(--ts-success-text);background:var(--ts-bg-green-500-a19)}
    .payment-tag.failed{color:var(--ts-text-red-200-5);background:var(--ts-bg-red-350-a22)}
    .payment-status-locked{color:var(--ts-warning-text);background:var(--ts-bg-orange-450-a16-2);border-color:var(--ts-border-orange-450-a50)}
    .payment-status-locked.paid{color:var(--ts-success-text);background:var(--ts-bg-green-500-a16);border-color:var(--ts-border-green-500-a50)}
    .payment-status-locked.failed{color:var(--ts-text-red-200-5);background:var(--ts-bg-red-350-a19);border-color:var(--ts-border-red-350-a50)}
    .payment-status-choice .radio-pill{color:var(--ts-text-sky-150-18);background:var(--ts-bg-navy-850-a60);border-color:var(--ts-border-navy-750-7)}
    .payment-status-choice .radio-pill:has(input:checked){border-color:var(--ts-border-blue-450-2);background:var(--ts-bg-blue-550-a18);color:var(--ts-text-sky-200-15)}
    .external-payment-fields{background:var(--ts-bg-green-500-a13);border-color:var(--ts-border-green-500-a50)}
    .external-payment-header{border-bottom-color:var(--ts-border-green-500-a22)}
    .external-payment-header i{color:var(--ts-success-text)}
    .external-payment-header strong{color:var(--ts-text-green-150-2)}
    .external-payment-header span{color:var(--ts-text-green-300-8)}
    .external-payment-notice{border-top-color:var(--ts-border-green-500-a22);color:var(--ts-text-green-300-8)}
    .external-payment-notice i{color:var(--ts-success-text)}
    .receipt-list li{background:var(--ts-bg-navy-850-38);border-color:var(--ts-border-navy-750-7);color:var(--ts-text-sky-150-19)}
    .reservation-actions{background:var(--ts-bg-navy-900-16);border-top-color:var(--ts-border-strong)}
    .icon-btn{color:var(--ts-text-sky-150);background:var(--ts-bg-navy-800);border-color:var(--ts-divider)}.icon-btn:hover{background:var(--ts-bg-navy-750-4)}
    .icon-btn.danger{color:var(--ts-text-red-150-11);background:var(--ts-bg-red-850);border-color:var(--ts-border-red-750)}.icon-btn.danger:hover{background:var(--ts-bg-red-800)}
    .reservation-id,.reservation-details span{color:var(--ts-text-sky-350-28)}
    .spin{display:inline-block;animation:reservaSpin .7s linear infinite}
    @keyframes reservaSpin{to{transform:rotate(360deg)}}

    .form-actions .btn-primary{min-height:40px;padding:0 1.25rem;color:var(--ts-on-primary);background:var(--ts-bg-blue-550);border:none;border-radius:8px;font-weight:750;cursor:pointer}
    .form-actions .btn-primary:hover:not(:disabled){background:var(--ts-bg-sky-500)}
    .form-actions .btn-primary:disabled{opacity:.6;cursor:not-allowed}
    .form-container.edit-reservation-modal .reason-error{border-color:var(--ts-border-red-350-4)!important}

    .group-detail-modal{width:min(1560px,97vw);max-height:90vh;display:flex;flex-direction:column;overflow:hidden}
    .group-detail-filter-panel{flex:0 0 auto;margin-bottom:1rem}
    .group-detail-filters{flex-wrap:nowrap}
    .group-detail-filters label{flex:1 1 160px;min-width:0}
    .group-detail-filters label:first-child{flex:3 1 260px}
    .group-detail-filter-actions{display:flex;flex:0 0 auto;gap:.5rem;align-items:stretch;margin-left:auto}
    .group-detail-filter-actions .btn-refresh{white-space:nowrap}
    .group-detail-filter-actions .icon-only{display:inline-flex;align-items:center;justify-content:center;width:2.6rem;min-width:2.6rem;padding:0;font-size:1.05rem}
    .group-detail-body{flex:1 1 auto;overflow-y:auto;overflow-x:hidden;min-height:320px;margin-bottom:1rem}
    /* La fila reorganizada (reserva/cliente arriba, total y acciones debajo)
       se aplica SIEMPRE dentro de este modal, sin importar el ancho real de
       la ventana -- a diferencia de la lista principal (pantalla completa,
       sin el recorte extra del padding del modal), aquí no hay forma de
       garantizar que la fila de una sola línea siempre quepa, así que se
       prioriza que nunca se corte ni haga falta scroll horizontal. */
    .group-detail-body ::ng-deep .management-record{grid-template-columns:46px minmax(150px,1fr) minmax(190px,1.2fr) auto !important}
    .group-detail-body ::ng-deep .management-record__status{grid-column:2 / 4;display:flex;align-items:center;flex-wrap:wrap;gap:.5rem}
    .group-detail-body ::ng-deep .management-record__actions{grid-column:4;grid-row:1 / 3}
    @media(max-width:720px){
      .group-detail-filters{flex-wrap:wrap}
      .group-detail-filters label{flex-basis:100%}
      .group-detail-filter-actions{width:100%;margin-left:0}
      .group-detail-filter-actions .btn-refresh{flex:1 1 0}
      .group-detail-filter-actions .icon-only{flex:0 0 auto}
      .group-detail-body ::ng-deep .management-record{grid-template-columns:42px minmax(0,1fr) auto !important;gap:.7rem;padding:.75rem}
      .group-detail-body ::ng-deep .management-record__primary{grid-column:2}
      .group-detail-body ::ng-deep .management-record__status{grid-column:3;grid-row:1;align-self:start}
      .group-detail-body ::ng-deep .management-record__summary{grid-column:1/-1;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.25rem .7rem;padding:.6rem .7rem;border-radius:10px;background:var(--ts-surface-soft)}
      .group-detail-body ::ng-deep .management-record__actions{grid-column:1/-1;grid-row:auto;width:100%}
      .group-detail-body ::ng-deep [recordActions]{display:grid;grid-template-columns:repeat(auto-fit,minmax(84px,1fr));gap:.5rem;width:100%}
      .group-detail-body ::ng-deep [recordActions] button{width:100%;min-width:0;padding:.5rem .22rem!important;font-size:.62rem!important;white-space:nowrap}
    }
    /* padding:0 -- igual que el resto de modales propios de este archivo
       (create/edit/group-detail): el header queda a todo lo ancho (sin la
       franja de 32px del .form-container base alrededor) y cada bloque de
       abajo (aviso, acciones) maneja su propio margen horizontal. */
    .send-link-modal{width:min(480px,100%);padding:0;box-sizing:border-box}
    /* Barra superior sin color propio -- se funde con el fondo del modal en
       vez de verse como una franja aparte (a diferencia del resto de
       modales, que sí la usan para fijarla al hacer scroll). */
    .send-link-modal .form-header{background:transparent;border-bottom:none;box-shadow:none}
    /* Mismo lenguaje visual que .edit-notice (aviso con borde de color) en
       vez de un párrafo suelto -- consistente con el resto de avisos del
       panel y más fácil de leer de un vistazo. Margen superior propio: al
       quitarle el padding al contenedor, sin esto el aviso queda pegado
       justo debajo del título. */
    .send-link-message{display:flex;align-items:flex-start;gap:.6rem;margin:1.3rem 1.35rem 1.5rem;padding:.8rem .9rem;border-left:4px solid var(--ts-border-sky-500-3);border-radius:8px;color:var(--ts-text-sky-150);background:var(--ts-bg-navy-850-2);font-size:.83rem;line-height:1.55}
    .send-link-message i{flex:0 0 auto;margin-top:.15rem;color:var(--ts-text-blue-250)}
    /* "Cancelar" queda solo a la izquierda (margin-right:auto) y "Copiar
       enlace"+"Enviar enlace" se agrupan como una sola unidad a la derecha
       -- si el espacio no alcanza para las tres, el grupo entero baja de
       línea junto (nunca se separan entre sí ni queda un botón suelto y
       descentrado a medio camino, sin importar el ancho real de pantalla).
       En pantallas chicas se centran con más aire entre ellos en vez de
       estirarse a todo el ancho -- es un modal de 3 acciones, no un
       formulario, así que botones más pequeños y bien repartidos se ven
       más cuidados que tres barras completas apiladas. */
    .send-link-actions{display:flex;flex-wrap:wrap;align-items:center;gap:.9rem;row-gap:.7rem;margin:0 1.35rem 1.35rem}
    .send-link-actions .btn-secondary{margin-right:auto}
    .send-link-actions-primary{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:.75rem;min-width:0}
    .btn-copy-link{display:inline-flex;align-items:center;gap:.4rem;min-height:40px;padding:0 1rem;color:var(--ts-text-sky-150);background:var(--ts-bg-navy-800);border:1px solid var(--ts-divider);border-radius:8px;font-weight:700;white-space:nowrap;cursor:pointer;transition:background .2s ease,color .2s ease,border-color .2s ease}
    .btn-copy-link:hover:not(:disabled){background:var(--ts-bg-navy-750-4)}
    .btn-copy-link:disabled{opacity:.6;cursor:not-allowed}
    .btn-copy-link i.bi-clipboard-check{color:var(--ts-success-text)}
    @media(max-width:460px){
      .send-link-actions{justify-content:center;gap:.6rem}
      .send-link-actions .btn-secondary{margin-right:0}
      .send-link-actions-primary{justify-content:center;flex:1 1 100%;gap:.6rem}
      .send-link-actions-primary button{flex:0 1 auto;padding:0 .85rem;font-size:.82rem}
    }
    @media(max-width:340px){
      .send-link-actions .btn-secondary{padding:0 .85rem;font-size:.82rem}
    }
  `]
})
export class ReservasComponent implements OnInit {
  readonly MAX_RESERVATION_DATES = 366;
  readonly formatClockTime = formatClockTime;
  showCreateForm = signal(false);

  /** Abre el formulario de nueva reserva desde el encabezado del módulo (dashboard). */
  openCreateForm(spaceId: number | null = null): void {
    if (spaceId) {
      this.reservationForm.space_id = Number(spaceId);
      this.onSpaceChange();
    }
    this.showCreateForm.set(true);
  }
  reservationType: 'user' | 'guest' = 'user';
  isCreating = signal(false);
  isLoading = signal(false);
  isScheduleLoading = signal(false);
  availableDays: number[] | undefined;
  selectedDaySchedule: SpaceSchedule | null = null;
  selectedOpeningTime: string | undefined;
  selectedClosingTime: string | undefined;
  private selectedSpaceSchedules: SpaceSchedule[] = [];
  // Mismo indicador de "cuánto del día ya está reservado" que el calendario
  // de reserva del cliente (relleno por celda, ver styles.css
  // .app-day-occupancy) — antes solo existía ahí; al crear una reserva
  // desde el panel se elegía la fecha completamente a ciegas.
  dayOccupancyMap: Map<string, { occupiedMinutes: number; totalMinutes: number }> = new Map();
  multiDateMode = false;
  reservationDatesInput = '';
  multiDateScheduleError: string | null = null;
  multiDateEstimatedPricePerDay = 0;
  multiDateEstimatedHasDiscount = false;
  private pricePreviewRequestId = 0;
  multiDateEstimatedTotal(): number {
    return this.multiDateEstimatedPricePerDay * (this.reservationForm.reservation_dates?.length || 0);
  }
  // Mismo precio por ocurrencia (multiDateEstimatedPricePerDay) que ya se
  // calcula con tarifa escalonada -- para una sola fecha el total es
  // directamente ese valor, sin multiplicar por días.
  singleDateEstimatedTotal(): number {
    return this.multiDateEstimatedPricePerDay;
  }
  customPricingPreview(): { basePerDay: number; total: number } | null {
    const rawBase = this.reservationForm.custom_price_total;
    if (rawBase === null || rawBase === undefined || rawBase === '') return null;
    const days = this.multiDateMode ? Math.max(1, this.reservationForm.reservation_dates?.length || 1) : 1;
    const base = Number(rawBase || 0);
    if (!Number.isFinite(base)) return null;
    return { basePerDay: base / days, total: base };
  }
  editScheduleLoading = signal(false);
  editAvailableDays: number[] | undefined;
  editSelectedDaySchedule: SpaceSchedule | null = null;
  editSelectedOpeningTime: string | undefined;
  editSelectedClosingTime: string | undefined;
  private editSpaceSchedules: SpaceSchedule[] = [];
  editDayOccupancyMap: Map<string, { occupiedMinutes: number; totalMinutes: number }> = new Map();
  editingReservation = signal<Reservation | null>(null);
  isSavingReservation = signal(false);
  reasonError = signal(false);
  selectedMonth = signal(0);
  // El historial no debe ocultarse al cambiar de año. El usuario puede
  // aplicar el filtro explícitamente cuando necesite acotar resultados.
  selectedYear = signal<number | null>(null);
  selectedStatus = signal('');
  selectedPaymentStatus = signal('');
  selectedSpaceId = signal<number | null>(null);
  searchTerm = signal('');
  readonly reservationStatusTabs: AdminTabOption[] = [
    { id: '', label: 'Todas', icon: 'bi-collection' },
    { id: 'pending', label: 'Pendientes', icon: 'bi-hourglass-split' },
    { id: 'confirmed', label: 'Confirmadas', icon: 'bi-check2-circle' },
    { id: 'completed', label: 'Completadas', icon: 'bi-flag' },
    { id: 'cancelled', label: 'Canceladas', icon: 'bi-x-circle' },
  ];
  readonly months = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  readonly reservationsPageSizeOptions = [25, 50, 75, 100];
  @ViewChild('reasonField') reasonFieldRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('reservaFormRef') reservaFormRef!: NgForm;
  @ViewChild('reservaFormEl') reservaFormEl!: ElementRef<HTMLFormElement>;
  @ViewChild('editFormRef') editFormRef!: NgForm;
  @ViewChild('editFormEl') editFormEl!: ElementRef<HTMLFormElement>;
  editForm: any = {};
  editExtrasMap: Record<number, number> = {};

  // Edición/eliminación de TODO el conjunto a la vez (ver
  // updateReservationGroupByAdmin / recycleReservationGroupByAdmin). Modal
  // separado del de edición individual: el de grupo nunca toca fecha ni
  // espacio (cada fecha conserva los suyos), así que no necesita el
  // selector de fecha ni la consulta de horario por día.
  @ViewChild('groupEditFormRef') groupEditFormRef!: NgForm;
  @ViewChild('groupEditFormEl') groupEditFormEl!: ElementRef<HTMLFormElement>;
  editingGroup = signal<{ groupId: string; primary: Reservation; dateCount: number; dateRangeLabel: string; paymentSummary: 'paid' | 'pending' | 'mixed' } | null>(null);
  isSavingGroupReservation = signal(false);
  groupReasonError = signal(false);
  groupEditForm: any = {};
  groupEditExtrasMap: Record<number, number> = {};
  cancellingGroupId = signal<string | null>(null);
  sendingGroupLinkId = signal<string | null>(null);

  // Modal propio de "enviar/copiar enlace de pago" -- reemplaza el diálogo
  // genérico de confirmación para poder ofrecer un botón de copiar además
  // del de enviar por correo (ver sendPaymentLink/sendGroupPaymentLink).
  sendLinkModal = signal<{
    title: string;
    message: string;
    reservationId: number;
    scope: 'individual' | 'group';
    groupId?: string;
  } | null>(null);
  sendLinkBusy = signal(false);
  linkCopied = signal(false);
  private linkCopiedTimer?: ReturnType<typeof setTimeout>;

  // ---------------------------------------------------------------------
  // Modal "ver fechas del conjunto" -- consulta propia y paginada
  // (getReservationGroupDates), separada por completo de la lista
  // principal. Antes esto era un desplegable inline que traía TODAS las
  // fechas del grupo dentro de la respuesta de la lista general; con
  // negocios reales (conjuntos de 20+ fechas) esa consulta se volvía
  // pesada aunque el admin nunca desplegara la tarjeta.
  // ---------------------------------------------------------------------
  groupDetailModal = signal<{ groupId: string; spaceTitle: string } | null>(null);
  groupDetailLoading = signal(false);
  groupDetailReservations = signal<Reservation[]>([]);
  groupDetailPage = signal(1);
  groupDetailPages = signal(1);
  groupDetailTotal = signal(0);
  groupDetailPageSize = signal(25);
  readonly groupDetailPageSizeOptions = [10, 25, 50, 100];
  groupDetailSearch = signal('');

  selectedGroupEditSpace = computed(() => {
    const group = this.editingGroup();
    if (!group) return undefined;
    return this.salaService.espacios().find(space => Number(space.space_id) === Number(group.primary?.space_id));
  });
  groupEditSpaceExtras = computed(() => this.selectedGroupEditSpace()?.extras || []);

  // Reservaciones
  reservaciones = signal<Reservation[]>([]);
  reservationsPage = signal(1);
  reservationsPages = signal(0);
  reservationsTotal = signal(0);
  reservationsPageSize = signal(25);
  cancellingId = signal<number | null>(null);

  // ---------------------------------------------------------------------
  // Agrupación de reservas multi-día: reservaciones() ya trae, para cada
  // grupo presente en la página actual, TODAS sus filas juntas (ver
  // getAdminReservations en el backend) -- aquí solo se arma la vista: una
  // tarjeta por reservation_group_id (o suelta si no tiene grupo).
  // ---------------------------------------------------------------------
  // El backend (getAdminReservations) ya NO envía todas las filas de cada
  // conjunto -- para un grupo, esta lista trae UNA fila representativa con
  // `group_summary` (total, saldo pendiente, rango de fechas, resumen de
  // estados/pago) ya calculado. El desglose fecha por fecha vive en su
  // propia consulta paginada (modal "ver fechas", ver openGroupDetail) que
  // solo se dispara si el admin la abre -- antes esa lista pesada viajaba
  // siempre, aunque nunca se desplegara.
  groupedReservaciones = computed(() =>
    this.reservaciones().map(reservation =>
      reservation.group_summary
        ? { type: 'group' as const, groupId: reservation.reservation_group_id!, primary: reservation, summary: reservation.group_summary }
        : { type: 'single' as const, reservation },
    ),
  );

  trackByCard = (_index: number, card: { type: 'single' | 'group'; reservation?: Reservation; groupId?: string }): string =>
    card.type === 'group' ? `g-${card.groupId}` : `s-${card.reservation!.reservation_id}`;

  trackByReservationId = (_index: number, reserva: Reservation): number => reserva.reservation_id;

  trackByStatus = (_index: number, item: { status: string }): string => item.status;

  reservationForm: any = {
    user_id: null,
    space_id: null,
    reservation_date: '',
    reservation_dates: [] as string[],
    start_time: '',
    end_time: '',
    number_of_people: 1,
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    payment_status: 'pending',
    external_payment_method: '',
    external_paid_at: '',
    external_notes: '',
    custom_price_total: null,
    receipts: [] as Array<{ file_name: string; original_name?: string; file_type?: string; mime?: string }>,
    extras: []
  };

  selectedExtrasMap: { [key: number]: number } = {};

  selectedSpace = computed(() => {
    const spaceId = this.reservationForm.space_id;
    if (!spaceId) return undefined;
    return this.salaService.espacios().find(e => Number(e.space_id) === Number(spaceId));
  });
  selectedSpaceExtras = computed(() => this.selectedSpace()?.extras || []);

  selectedEditSpace = computed(() =>
    this.salaService.espacios().find(space => Number(space.space_id) === Number(this.editForm.space_id))
  );

  editSpaceExtras = computed(() => this.selectedEditSpace()?.extras || []);

  constructor(
    public reservationService: ReservationService,
    public customerService: CustomerService,
    public salaService: SalaService,
    private authService: AuthService,
    private dialogs: DialogService,
    private spaceScheduleService: SpaceScheduleService,
    private editService: ReservationEditService,
    private notifications: NotificationService,
    private cooldown: ActionCooldownService,
  ) {}

  ngOnInit(): void {
    this.cargarDatos();
    const pending = this.editService.consume();
    if (pending) {
      this.openEditReservation(pending);
    }
  }

  cargarDatos(): void {
    // Cargar espacios (una sola vez por sesión: sala-detalle ya refresca este
    // mismo signal compartido tras crear/editar/eliminar un espacio, así que
    // recargarlo aquí en cada visita a la pestaña es redundante).
    if (!this.salaService.espaciosInitialized() && !this.salaService.espaciosLoading()) {
      this.salaService.loadEspacios();
    }

    // Cargar clientes si hay business_id
    const businessId = this.getBusinessId();
    if (businessId) {
      this.customerService.loadClientes(businessId);
    }

    // Cargar reservaciones del calendario
    this.cargarReservaciones();
  }

  cargarReservaciones(): void {
    this.cargarReservacionesPorPagina(this.reservationsPage());
  }

  private cargarReservacionesPorPagina(page: number): void {
    const loadingStartedAt = Date.now();
    this.isLoading.set(true);
    const year = this.selectedYear();
    this.reservationService.getAdminReservations({
      page,
      limit: this.reservationsPageSize(),
      status: this.selectedStatus() || undefined,
      payment_status: this.selectedPaymentStatus() || undefined,
      search: this.searchTerm() || undefined,
      month: this.selectedMonth() > 0 ? this.selectedMonth() : undefined,
      year: year && year >= 2020 && year <= 2100 ? year : undefined,
      space_id: this.selectedSpaceId() || undefined,
    }).subscribe({
      next: response => {
        this.reservaciones.set(response.reservations || []);
        this.reservationsTotal.set(Number(response.count || 0));
        this.reservationsPages.set(Math.max(1, Number(response.pages || 1)));
        this.reservationsPage.set(Math.max(1, Number(response.page || page)));
        finishLoadingAfter(loadingStartedAt, () => this.isLoading.set(false));
      },
      error: (error) => {
        this.reservaciones.set([]);
        this.reservationsTotal.set(0);
        this.reservationsPages.set(1);
        this.notifications.error(this.getFriendlyError(error, 'No fue posible cargar las reservas.'));
        finishLoadingAfter(loadingStartedAt, () => this.isLoading.set(false));
      }
    });
  }

  goToReservationsPage(page: number): void {
    if (this.isLoading() || page < 1 || page > this.reservationsPages() || page === this.reservationsPage()) return;
    this.cargarReservacionesPorPagina(page);
  }

  applyFilters(): void {
    if (!this.cooldown.consume('res-filter')) return;
    this.cargarReservacionesPorPagina(1);
  }

  onSearchChange(value: string): void { this.searchTerm.set(String(value || '').trimStart()); }
  onStatusChange(value: string): void { this.selectedStatus.set(String(value || '')); this.applyFilters(); }
  onPaymentStatusChange(value: string): void { this.selectedPaymentStatus.set(String(value || '')); this.applyFilters(); }
  onMonthChange(value: number): void { this.selectedMonth.set(Number(value) || 0); this.applyFilters(); }
  onSpaceFilterChange(value: number | string): void {
    const parsed = Number(value);
    this.selectedSpaceId.set(Number.isInteger(parsed) && parsed > 0 ? parsed : null);
    this.applyFilters();
  }
  onYearChange(value: number | string): void {
    const parsed = Number(value);
    this.selectedYear.set(Number.isInteger(parsed) && parsed >= 2020 && parsed <= 2100 ? parsed : null);
    this.applyFilters();
  }
  onPageSizeChange(value: number | string): void {
    const parsed = Number(value);
    if (![25, 50, 75, 100].includes(parsed)) return;
    this.reservationsPageSize.set(parsed);
    this.applyFilters();
  }

  getBusinessId(): number | null {
    const business = this.authService.currentUser();
    return business?.id || null;
  }

  onReservationTypeChange(): void {
    // Limpiar campos cuando cambia el tipo
    if (this.reservationType === 'user') {
      this.reservationForm.guest_name = '';
      this.reservationForm.guest_phone = '';
      this.reservationForm.guest_email = '';
      this.reservationForm.user_id = null;
    } else {
      this.reservationForm.user_id = null;
    }
  }

  onSpaceChange(): void {
    this.selectedExtrasMap = {};
    this.reservationForm.space_id = Number(this.reservationForm.space_id) || null;
    this.reservationForm.reservation_date = '';
    this.resetSelectedSchedule();
    this.dayOccupancyMap = new Map();

    if (!this.reservationForm.space_id) {
      this.availableDays = undefined;
      this.selectedSpaceSchedules = [];
      return;
    }

    this.isScheduleLoading.set(true);
    this.spaceScheduleService.getSchedulesBySpace(this.reservationForm.space_id).subscribe({
      next: response => {
        this.selectedSpaceSchedules = response.schedules || [];
        const openSchedules = this.selectedSpaceSchedules.filter(schedule => !schedule.is_closed);
        this.availableDays = this.selectedSpaceSchedules.length
          ? openSchedules.map(schedule => schedule.day_of_week)
          : [0, 1, 2, 3, 4, 5, 6];
        this.isScheduleLoading.set(false);
        this.fetchDayOccupancy(this.reservationForm.space_id!, this.selectedSpaceSchedules, map => this.dayOccupancyMap = map);
      },
      error: error => {
        this.isScheduleLoading.set(false);
        this.availableDays = [];
        this.selectedSpaceSchedules = [];
        this.notifications.error(this.getFriendlyError(error, 'No se pudieron consultar los horarios del espacio.'));
      }
    });
  }

  // Cruza los días ya reservados (endpoint público de disponibilidad, el
  // mismo que usa el cliente) con el horario del espacio ese día de la
  // semana, para saber qué fracción de la jornada ya está ocupada — la
  // lectura que pinta el indicador de capacidad de cada día del
  // calendario. Silencioso ante error a propósito: es una ayuda visual
  // extra, no algo bloqueante para crear/editar la reserva.
  private fetchDayOccupancy(spaceId: number, schedules: SpaceSchedule[], assign: (map: Map<string, { occupiedMinutes: number; totalMinutes: number }>) => void): void {
    this.reservationService.obtenerHorariosOcupados(spaceId).subscribe({
      next: response => {
        const map = new Map<string, { occupiedMinutes: number; totalMinutes: number }>();
        const minutesBetween = (start: string, end: string): number => {
          const [sh, sm] = start.split(':').map(Number);
          const [eh, em] = end.split(':').map(Number);
          return (eh * 60 + em) - (sh * 60 + sm);
        };
        // Los días/horarios no disponibles (asueto, mantenimiento...) cuentan
        // como ocupados: un bloqueo de todo el día deja la fecha completa
        // (deshabilitada en el calendario), uno parcial resta esas horas.
        const slotsByDate = new Map<string, Array<{ start: string; end: string }>>();
        for (const day of [...(response.occupied_days ?? []), ...(response.blocked_days ?? [])]) {
          slotsByDate.set(day.date, [...(slotsByDate.get(day.date) ?? []), ...day.slots]);
        }
        const blockedDates = new Set((response.blocked_days ?? []).map(day => day.date));
        for (const [date, slots] of slotsByDate) {
          const [year, month, dayNum] = date.split('-').map(Number);
          const dayOfWeek = new Date(year, month - 1, dayNum).getDay();
          const schedule = schedules.find(item => item.day_of_week === dayOfWeek);
          // Sin horario configurado el espacio opera todo el día (mismo criterio
          // que el backend); solo se aplica a los bloqueos para no cambiar el
          // indicador de reservas de esos espacios.
          const window = schedule && !schedule.is_closed
            ? { open: schedule.opening_time.slice(0, 5), close: schedule.closing_time.slice(0, 5) }
            : (!schedules.length && blockedDates.has(date) ? { open: '00:00', close: '23:59' } : null);
          if (!window) continue;
          const totalMinutes = minutesBetween(window.open, window.close);
          if (totalMinutes <= 0) continue;
          // Unión de franjas recortadas al horario (sin contar dos veces solapes).
          const ranges = slots
            .map(slot => [slot.start.slice(0, 5) < window.open ? window.open : slot.start.slice(0, 5), slot.end.slice(0, 5) > window.close ? window.close : slot.end.slice(0, 5)])
            .filter(([start, end]) => end > start)
            .sort(([a], [b]) => a.localeCompare(b));
          let occupiedMinutes = 0;
          let cursor = window.open;
          for (const [start, end] of ranges) {
            const from = start > cursor ? start : cursor;
            if (end > from) { occupiedMinutes += minutesBetween(from, end); cursor = end; }
          }
          if (occupiedMinutes <= 0) continue;
          map.set(date, { occupiedMinutes: Math.min(occupiedMinutes, totalMinutes), totalMinutes });
        }
        assign(map);
      },
      error: () => assign(new Map())
    });
  }

  onReservationDateChange(date: string): void {
    this.reservationForm.reservation_date = date;
    this.reservationForm.start_time = '';
    this.reservationForm.end_time = '';
    this.resetSelectedSchedule();
    if (!date) return;

    const [year, month, day] = date.split('-').map(Number);
    const dayOfWeek = new Date(year, month - 1, day).getDay();
    const configuredSchedule = this.selectedSpaceSchedules.find(
      schedule => schedule.day_of_week === dayOfWeek
    );

    if (this.selectedSpaceSchedules.length === 0) {
      this.selectedDaySchedule = {
        schedule_id: 0,
        space_id: this.reservationForm.space_id,
        day_of_week: dayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6,
        opening_time: '00:00',
        closing_time: '23:59',
        is_closed: false
      };
    } else if (configuredSchedule && !configuredSchedule.is_closed) {
      this.selectedDaySchedule = configuredSchedule;
    } else {
      // Si el espacio ya configuró horarios, un día ausente o cerrado no se
      // convierte en 24/7. Debe coincidir con backend y con el portal público.
      this.resetSelectedSchedule();
      return;
    }

    if (this.selectedDaySchedule) {
      const opening = this.futureOpeningForDate(date, this.normalizeTime(this.selectedDaySchedule.opening_time));
      this.selectedClosingTime = this.normalizeTime(this.selectedDaySchedule.closing_time);
      if (!opening || opening >= this.selectedClosingTime) {
        this.resetSelectedSchedule();
        return;
      }
      this.selectedOpeningTime = opening;
    }
  }

  onReservationTimeChange(): void {
    if (
      this.reservationForm.start_time &&
      this.reservationForm.end_time &&
      this.reservationForm.start_time >= this.reservationForm.end_time
    ) {
      this.reservationForm.end_time = '';
    }
    this.updateMultiDatePricePreview();
  }

  onMultiDateModeChange(): void {
    this.reservationForm.reservation_date = '';
    this.reservationForm.reservation_dates = [];
    this.reservationDatesInput = '';
    this.reservationForm.start_time = '';
    this.reservationForm.end_time = '';
    this.multiDateScheduleError = null;
    this.multiDateEstimatedPricePerDay = 0;
    this.resetSelectedSchedule();
  }

  // Ventana de horario habilitado para UNA fecha, replicando la misma regla
  // que onReservationDateChange: sin horarios configurados, el espacio opera
  // todo el día; si hay configuración, ese día debe existir y no estar cerrado.
  private getDayScheduleWindow(dateStr: string): { opening: string; closing: string } | null {
    const [year, month, day] = dateStr.split('-').map(Number);
    const dayOfWeek = new Date(year, month - 1, day).getDay();
    if (this.selectedSpaceSchedules.length === 0) return { opening: '00:00', closing: '23:59' };
    const configured = this.selectedSpaceSchedules.find(schedule => schedule.day_of_week === dayOfWeek);
    if (!configured || configured.is_closed) return null;
    return { opening: this.normalizeTime(configured.opening_time), closing: this.normalizeTime(configured.closing_time) };
  }

  // Varios días seleccionados comparten el MISMO rango de hora (un solo par
  // inicio/fin para toda la reserva), así que el rango válido es la
  // intersección de los horarios habilitados de cada día elegido.
  onReservationDatesChange(dates: string[]): void {
    this.reservationForm.reservation_dates = dates;
    this.reservationForm.start_time = '';
    this.reservationForm.end_time = '';
    this.resetSelectedSchedule();
    this.multiDateScheduleError = null;
    if (!dates.length) return;

    let intersectionOpen = '00:00';
    let intersectionClose = '23:59';
    for (const dateStr of dates) {
      const window = this.getDayScheduleWindow(dateStr);
      if (!window) {
        this.multiDateScheduleError = `El espacio no está disponible el ${this.formatDate(dateStr)}. Quítalo de la selección o elige otro día.`;
        return;
      }
      const opening = this.futureOpeningForDate(dateStr, window.opening);
      if (!opening || opening >= window.closing) {
        this.multiDateScheduleError = `Ya no quedan horarios disponibles para hoy.`;
        return;
      }
      if (opening > intersectionOpen) intersectionOpen = opening;
      if (window.closing < intersectionClose) intersectionClose = window.closing;
    }
    if (intersectionOpen >= intersectionClose) {
      this.multiDateScheduleError = 'Los días seleccionados no comparten un horario en común. Elige días con horarios compatibles.';
      return;
    }
    this.selectedDaySchedule = { schedule_id: 0, space_id: this.reservationForm.space_id, day_of_week: 0, opening_time: intersectionOpen, closing_time: intersectionClose, is_closed: false };
    this.selectedOpeningTime = intersectionOpen;
    this.selectedClosingTime = intersectionClose;
  }

  // Antes multiplicaba precio_por_hora × horas a mano -- un espacio con
  // tarifas escalonadas mostraba al empleado un estimado distinto al monto
  // real que termina cobrándose (el backend sí calcula con tarifas al crear
  // la reserva). Ahora pide el precio real al mismo endpoint que usa el
  // cliente en el checkout, para que el estimado y el cobro real coincidan
  // siempre. Se usa tanto para una fecha como para varias (el precio es "por
  // día"; el total multi-día se multiplica en la plantilla).
  private updateMultiDatePricePreview(): void {
    const space = this.selectedSpace();
    const start = this.reservationForm.start_time;
    const end = this.reservationForm.end_time;
    const requestId = ++this.pricePreviewRequestId;
    if (!space || !start || !end || start >= end) { this.multiDateEstimatedPricePerDay = 0; return; }
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);
    const hours = (endHour * 60 + endMinute - (startHour * 60 + startMinute)) / 60;
    if (hours <= 0) { this.multiDateEstimatedPricePerDay = 0; return; }
    this.salaService.calcularPrecioPorHoras(space.space_id, hours).subscribe({
      next: (result) => {
        if (requestId !== this.pricePreviewRequestId) return; // el usuario ya cambió el horario de nuevo
        this.multiDateEstimatedPricePerDay = Number(result.final_price);
        this.multiDateEstimatedHasDiscount = result.pricing_method !== 'Precio por hora';
      },
      error: () => {
        if (requestId !== this.pricePreviewRequestId) return;
        // Sin conexión al cálculo real, se conserva un estimado simple en
        // vez de dejar el total en blanco -- se aclara en la plantilla que
        // es aproximado.
        this.multiDateEstimatedPricePerDay = hours * Number(space.price_per_hour || 0);
        this.multiDateEstimatedHasDiscount = false;
      },
    });
  }

  private resetSelectedSchedule(): void {
    this.selectedDaySchedule = null;
    this.selectedOpeningTime = undefined;
    this.selectedClosingTime = undefined;
  }

  private normalizeTime(time: string): string {
    return String(time || '').slice(0, 5);
  }

  private salvadorNow(): { date: string; minutes: number } {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/El_Salvador', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date());
    const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value || 0);
    const year = value('year');
    const month = value('month');
    const day = value('day');
    return {
      date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      minutes: value('hour') * 60 + value('minute'),
    };
  }

  private futureOpeningForDate(date: string, opening: string): string | null {
    const now = this.salvadorNow();
    if (date !== now.date) return opening;
    const nextHalfHour = Math.ceil((now.minutes + 1) / 30) * 30;
    if (nextHalfHour >= 1440) return null;
    const openingMinutes = this.timeToMinutes(opening);
    const result = Math.max(openingMinutes, nextHalfHour);
    return `${String(Math.floor(result / 60)).padStart(2, '0')}:${String(result % 60).padStart(2, '0')}`;
  }

  private isPastStart(date: string, start: string): boolean {
    const now = this.salvadorNow();
    if (date < now.date) return true;
    return date === now.date && this.timeToMinutes(start) <= now.minutes;
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = String(time || '').slice(0, 5).split(':').map(Number);
    return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : -1;
  }

  toggleExtra(extraId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedExtrasMap[extraId] = 1;
    } else {
      delete this.selectedExtrasMap[extraId];
    }
  }

  isExtraSelected(extraId: number): boolean {
    return extraId in this.selectedExtrasMap;
  }

  updateExtraQuantity(extraId: number, value: any): void {
    const numValue = typeof value === 'number' ? value : parseInt(value);
    if (numValue > 0 && !isNaN(numValue)) {
      this.selectedExtrasMap[extraId] = numValue;
    } else {
      delete this.selectedExtrasMap[extraId];
    }
  }

  onSubmit(): void {
    if (this.multiDateMode && this.reservationForm.reservation_dates.length > this.MAX_RESERVATION_DATES) {
      this.notifications.error(`Puedes incluir hasta ${this.MAX_RESERVATION_DATES} días en una misma reserva.`);
      return;
    }
    if (!validateAndFocusFirstError(this.reservaFormRef, this.reservaFormEl?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de crear la reserva.')) {
      return;
    }

    if (this.multiDateMode && this.multiDateScheduleError) {
      this.notifications.error(this.multiDateScheduleError);
      return;
    }

    const selectedDates: string[] = this.multiDateMode
      ? this.reservationForm.reservation_dates
      : [this.reservationForm.reservation_date];
    if (selectedDates.some(date => this.isPastStart(date, this.reservationForm.start_time))) {
      focusFieldWithError(
        this.reservaFormEl?.nativeElement,
        '[name="startTime"]',
        msg => this.notifications.error(msg),
        'La hora de inicio ya transcurrió. Elige un horario posterior a la hora actual.'
      );
      return;
    }

    if (!this.selectedDaySchedule || !this.selectedOpeningTime || !this.selectedClosingTime) {
      focusFieldWithError(this.reservaFormEl?.nativeElement, '[name="reservationDate"], input[appDatePicker="date"]', msg => this.notifications.error(msg), 'El espacio no está disponible en el día seleccionado');
      return;
    }

    if (
      this.reservationForm.start_time < this.selectedOpeningTime ||
      this.reservationForm.end_time > this.selectedClosingTime
    ) {
      focusFieldWithError(this.reservaFormEl?.nativeElement, '[name="startTime"]', msg => this.notifications.error(msg), `Selecciona un horario entre ${this.selectedOpeningTime} y ${this.selectedClosingTime}`);
      return;
    }

    if (this.reservationForm.start_time >= this.reservationForm.end_time) {
      focusFieldWithError(this.reservaFormEl?.nativeElement, '[name="endTime"]', msg => this.notifications.error(msg), 'La hora de fin debe ser posterior a la hora de inicio');
      return;
    }

    const selectedSpace = this.salaService.espacios().find(
      space => Number(space.space_id) === Number(this.reservationForm.space_id)
    );
    if (
      !Number.isInteger(Number(this.reservationForm.number_of_people)) ||
      Number(this.reservationForm.number_of_people) < 1 ||
      (selectedSpace && Number(this.reservationForm.number_of_people) > Number(selectedSpace.capacity))
    ) {
      focusFieldWithError(this.reservaFormEl?.nativeElement, '[name="numberOfPeople"]', msg => this.notifications.error(msg), `La cantidad debe estar entre 1 y ${selectedSpace?.capacity || 1} personas`);
      return;
    }

    // Preparar extras
    const extras = Object.keys(this.selectedExtrasMap).map(extraId => ({
      extra_id: parseInt(extraId),
      quantity: this.selectedExtrasMap[parseInt(extraId)]
    }));

    this.isCreating.set(true);

    if (this.multiDateMode) {
      // El backend valida cada fecha (horario habilitado + choques de horario)
      // dentro de una sola transacción todo-o-nada, así que no hace falta
      // repetir aquí la verificación previa fecha por fecha.
      this.createReservation(extras);
      return;
    }

    this.reservationService.verificarDisponibilidad(
      Number(this.reservationForm.space_id),
      this.reservationForm.reservation_date,
      this.reservationForm.start_time,
      this.reservationForm.end_time,
      Number(this.reservationForm.number_of_people)
    ).subscribe({
      next: availability => {
        if (!availability.available) {
          this.isCreating.set(false);
          this.notifications.error(
            availability.message || 'El horario seleccionado ya no está disponible'
          );
          return;
        }
        this.createReservation(extras);
      },
      error: error => {
        this.isCreating.set(false);
        this.notifications.error(this.getFriendlyError(error, 'No fue posible verificar la disponibilidad del horario.'));
      }
    });
  }

  private async createReservation(extras: Array<{ extra_id: number; quantity: number }>): Promise<void> {
    if (!this.cooldown.consume('create-reservation')) return;
    const dateFields = this.multiDateMode
      ? { reservation_dates: this.reservationForm.reservation_dates }
      : { reservation_date: this.reservationForm.reservation_date };
    const action$ = this.reservationType === 'user'
      ? this.reservationService.crearReservacionParaUsuario({
          user_id: this.reservationForm.user_id,
          space_id: this.reservationForm.space_id,
          ...dateFields,
          start_time: this.reservationForm.start_time,
          end_time: this.reservationForm.end_time,
          number_of_people: this.reservationForm.number_of_people,
          custom_price_total: this.reservationForm.custom_price_total === '' ? null : this.reservationForm.custom_price_total,
          extras: extras.length > 0 ? extras : undefined,
          ...this.externalPaymentPayload(this.reservationForm),
        } as CreateReservationForUserRequest)
      : this.reservationService.crearReservacionParaInvitado({
          space_id: this.reservationForm.space_id,
          ...dateFields,
          start_time: this.reservationForm.start_time,
          end_time: this.reservationForm.end_time,
          number_of_people: this.reservationForm.number_of_people,
          guest_name: this.reservationForm.guest_name,
          guest_phone: this.reservationForm.guest_phone,
          guest_email: this.reservationForm.guest_email || undefined,
          custom_price_total: this.reservationForm.custom_price_total === '' ? null : this.reservationForm.custom_price_total,
          extras: extras.length > 0 ? extras : undefined,
          ...this.externalPaymentPayload(this.reservationForm),
        } as CreateReservationForGuestRequest);

    const dayCount = this.multiDateMode ? this.reservationForm.reservation_dates.length : 1;
    try {
      const reserva = await this.dialogs.confirmAndRun(
        {
          title: 'Confirmar reservación',
          message: dayCount > 1
            ? `¿Confirmas que deseas crear esta reservación para ${dayCount} días? Se generará un solo pago y un solo comprobante con el detalle de todas las fechas.`
            : '¿Confirmas que deseas crear esta reservación?',
          tone: 'info', confirmText: 'Crear reservación',
        },
        () => action$,
        dayCount > 1 ? `Reservación de ${dayCount} días creada exitosamente` : 'Reservación creada exitosamente',
        error => this.getFriendlyError(error, 'No se pudo crear la reservación.'),
      );
      if (reserva) {
        this.cancelForm();
        this.cargarReservaciones();
      }
    } catch (error) {
      console.error('Error al crear reservación:', error);
    } finally {
      this.isCreating.set(false);
    }
  }

  // ---------------------------------------------------------------------
  // Pago externo (reserva "Pagada" cobrada al negocio por otro medio)
  // ---------------------------------------------------------------------

  // Devuelve los campos de pago externo solo cuando el admin marcó "Pagada".
  // `skip` se pasa en true cuando la reserva/conjunto YA estaba pagado por
  // OTRO origen que no es "external" (la pasarela directa o un enlace de
  // pago) -- en ese caso `form.payment_status` sigue en 'paid' (así se
  // inicializa el formulario al abrir una reserva ya pagada, sin importar
  // el origen), pero NUNCA se debe reenviar como confirmación de pago
  // externo: antes esto duplicaba el registro de pago (payment_origin
  // pasaba de 'platform' a 'external' en cada edición no relacionada) y
  // reenviaba el correo de "¡Pago confirmado!" al cliente sin que hubiera
  // pagado de nuevo.
  private externalPaymentPayload(form: any, skip = false): Record<string, unknown> {
    if (skip || form.payment_status !== 'paid') return {};
    return {
      payment_status: 'paid',
      payment_origin: 'external',
      external_payment_method: form.external_payment_method,
      external_paid_at: form.external_paid_at || undefined,
      external_notes: (form.external_notes || '').trim() || undefined,
      receipts: (form.receipts || []).length ? form.receipts : undefined,
    };
  }

  // Sube un comprobante y lo agrega a la lista del formulario indicado.
  private receiptFormFor(target: 'create' | 'edit' | 'groupEdit'): any {
    if (target === 'create') return this.reservationForm;
    if (target === 'groupEdit') return this.groupEditForm;
    return this.editForm;
  }

  onReceiptFileChange(event: Event, target: 'create' | 'edit' | 'groupEdit'): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    input.value = '';
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      return this.notifications.error('Solo se permiten imágenes (JPG, PNG, WEBP) o PDF.');
    }
    this.reservationService.subirComprobante(file).subscribe({
      next: (res) => {
        const receipt = {
          file_name: res.file_name,
          original_name: res.original_name || file.name,
          file_type: res.file_type,
          mime: res.mime,
        };
        const form = this.receiptFormFor(target);
        form.receipts = form.receipts || [];
        form.receipts.push(receipt);
      },
      error: (error) => this.notifications.error(String(error?.message || error || 'No fue posible subir el comprobante.')),
    });
  }

  removeReceipt(index: number, target: 'create' | 'edit' | 'groupEdit'): void {
    const form = this.receiptFormFor(target);
    form.receipts = (form.receipts || []).filter((_: unknown, i: number) => i !== index);
  }

  openEditReservation(reservation: Reservation): void {
    this.editForm = {
      space_id: Number(reservation.space_id),
      reservation_date: String(reservation.reservation_date || reservation.date || '').slice(0, 10),
      start_time: String(reservation.start_time || '').slice(0, 5),
      end_time: String(reservation.end_time || '').slice(0, 5),
      number_of_people: Number(reservation.number_of_people),
      status: reservation.status,
      guest_name: reservation.guest_name || '',
      guest_phone: String(reservation.customer?.phone_number || reservation.customer_phone || reservation.guest_phone || '').trim(),
      guest_email: reservation.guest_email || '',
      change_reason: '',
      payment_status: reservation.payment_status || 'pending',
      // Si ya se había confirmado un pago externo antes, se precarga aquí --
      // antes quedaba siempre en blanco al reabrir la reserva para editar,
      // dando la impresión de que esos datos se habían perdido.
      external_payment_method: reservation.payment_origin === 'external' ? (reservation.external_payment_method || '') : '',
      external_paid_at: reservation.payment_origin === 'external' ? String(reservation.external_paid_at || '').slice(0, 10) : '',
      external_notes: reservation.payment_origin === 'external' ? (reservation.external_notes || '') : '',
      receipts: [] as Array<{ file_name: string; original_name?: string; file_type?: string; mime?: string }>,
    };
    this.editExtrasMap = {};
    (reservation.extras || []).forEach(extra => this.editExtrasMap[extra.extra_id] = Number(extra.quantity) || 1);
    this.editAvailableDays = undefined;
    this.editSpaceSchedules = [];
    this.editDayOccupancyMap = new Map();
    this.editResetSelectedSchedule();
    const spaceId = Number(reservation.space_id);
    if (spaceId) {
      this.editScheduleLoading.set(true);
      this.spaceScheduleService.getSchedulesBySpace(spaceId).subscribe({
        next: response => {
          this.editSpaceSchedules = response.schedules || [];
          const openSchedules = this.editSpaceSchedules.filter(s => !s.is_closed);
          this.editAvailableDays = this.editSpaceSchedules.length ? openSchedules.map(s => s.day_of_week) : [0, 1, 2, 3, 4, 5, 6];
          this.editScheduleLoading.set(false);
          this.fetchDayOccupancy(spaceId, this.editSpaceSchedules, map => this.editDayOccupancyMap = map);
          if (this.editForm.reservation_date) {
            const [yr, mo, dy] = this.editForm.reservation_date.split('-').map(Number);
            const dow = new Date(yr, mo - 1, dy).getDay();
            const cfg = this.editSpaceSchedules.find(s => s.day_of_week === dow);
            if (this.editSpaceSchedules.length === 0) {
              this.editSelectedDaySchedule = { schedule_id: 0, space_id: this.editForm.space_id, day_of_week: dow as 0|1|2|3|4|5|6, opening_time: '00:00', closing_time: '23:59', is_closed: false };
            } else if (cfg && !cfg.is_closed) {
              this.editSelectedDaySchedule = cfg;
            }
            if (this.editSelectedDaySchedule) {
              this.editSelectedOpeningTime = this.normalizeTime(this.editSelectedDaySchedule.opening_time);
              this.editSelectedClosingTime = this.normalizeTime(this.editSelectedDaySchedule.closing_time);
            }
          }
        },
        error: () => {
          this.editScheduleLoading.set(false);
          this.editAvailableDays = [];
          this.editSpaceSchedules = [];
        }
      });
    }
    this.editingReservation.set(reservation);
    this.reasonError.set(false);
  }

  closeEditReservation(): void {
    if (this.isSavingReservation()) return;
    this.editingReservation.set(null);
    this.reasonError.set(false);
    this.editExtrasMap = {};
    this.editAvailableDays = undefined;
    this.editSpaceSchedules = [];
    this.editResetSelectedSchedule();
    this.editScheduleLoading.set(false);
  }

  onEditSpaceChange(): void {
    this.editExtrasMap = {};
    this.editForm.space_id = Number(this.editForm.space_id) || null;
    this.editForm.reservation_date = '';
    this.editForm.start_time = '';
    this.editForm.end_time = '';
    this.editResetSelectedSchedule();
    this.editDayOccupancyMap = new Map();
    if (!this.editForm.space_id) {
      this.editAvailableDays = undefined;
      this.editSpaceSchedules = [];
      return;
    }
    this.editScheduleLoading.set(true);
    this.spaceScheduleService.getSchedulesBySpace(this.editForm.space_id).subscribe({
      next: response => {
        this.editSpaceSchedules = response.schedules || [];
        const openSchedules = this.editSpaceSchedules.filter(s => !s.is_closed);
        this.editAvailableDays = this.editSpaceSchedules.length ? openSchedules.map(s => s.day_of_week) : [0, 1, 2, 3, 4, 5, 6];
        this.editScheduleLoading.set(false);
        this.fetchDayOccupancy(this.editForm.space_id!, this.editSpaceSchedules, map => this.editDayOccupancyMap = map);
      },
      error: () => {
        this.editScheduleLoading.set(false);
        this.editAvailableDays = [];
        this.editSpaceSchedules = [];
      }
    });
  }

  onEditDateChange(date: string): void {
    this.editForm.reservation_date = date;
    this.editForm.start_time = '';
    this.editForm.end_time = '';
    this.editResetSelectedSchedule();
    if (!date) return;
    const [year, month, day] = date.split('-').map(Number);
    const dayOfWeek = new Date(year, month - 1, day).getDay();
    const configuredSchedule = this.editSpaceSchedules.find(s => s.day_of_week === dayOfWeek);
    if (this.editSpaceSchedules.length === 0) {
      this.editSelectedDaySchedule = { schedule_id: 0, space_id: this.editForm.space_id, day_of_week: dayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6, opening_time: '00:00', closing_time: '23:59', is_closed: false };
    } else if (configuredSchedule && !configuredSchedule.is_closed) {
      this.editSelectedDaySchedule = configuredSchedule;
    }
    if (this.editSelectedDaySchedule) {
      this.editSelectedOpeningTime = this.normalizeTime(this.editSelectedDaySchedule.opening_time);
      this.editSelectedClosingTime = this.normalizeTime(this.editSelectedDaySchedule.closing_time);
    }
  }

  onEditTimeChange(): void {
    if (this.editForm.start_time && this.editForm.end_time && this.editForm.start_time >= this.editForm.end_time) {
      this.editForm.end_time = '';
    }
  }

  private editResetSelectedSchedule(): void {
    this.editSelectedDaySchedule = null;
    this.editSelectedOpeningTime = undefined;
    this.editSelectedClosingTime = undefined;
  }

  isEditExtraSelected(extraId: number): boolean {
    return extraId in this.editExtrasMap;
  }

  toggleEditExtra(extraId: number, event: Event): void {
    if ((event.target as HTMLInputElement).checked) this.editExtrasMap[extraId] = 1;
    else delete this.editExtrasMap[extraId];
  }

  updateEditExtraQuantity(extraId: number, value: unknown): void {
    const quantity = Number(value);
    if (Number.isInteger(quantity) && quantity > 0) this.editExtrasMap[extraId] = quantity;
  }

  // ---------------------------------------------------------------------
  // Edición/eliminación de TODO el conjunto (reserva multi-día) a la vez
  // ---------------------------------------------------------------------

  openEditGroup(card: { groupId: string; primary: Reservation; summary: NonNullable<Reservation['group_summary']> }): void {
    const primary = card.primary;
    this.groupEditForm = {
      start_time: String(primary.start_time || '').slice(0, 5),
      end_time: String(primary.end_time || '').slice(0, 5),
      number_of_people: Number(primary.number_of_people),
      status: primary.status,
      guest_name: primary.guest_name || '',
      guest_phone: String(primary.customer?.phone_number || primary.customer_phone || primary.guest_phone || '').trim(),
      guest_email: primary.guest_email || '',
      change_reason: '',
      payment_status: card.summary.payment_summary === 'paid' ? 'paid' : 'pending',
      external_payment_method: '',
      external_paid_at: '',
      external_notes: '',
      custom_price_total: null,
      receipts: [] as Array<{ file_name: string; original_name?: string; file_type?: string; mime?: string }>,
    };
    this.groupEditExtrasMap = {};
    (primary.extras || []).forEach(extra => this.groupEditExtrasMap[extra.extra_id] = Number(extra.quantity) || 1);
    this.groupReasonError.set(false);
    this.editingGroup.set({
      groupId: card.groupId,
      primary,
      dateCount: card.summary.date_count,
      dateRangeLabel: card.summary.date_count === 1 ? this.formatDate(card.summary.start_date) : `${this.formatDate(card.summary.start_date)} – ${this.formatDate(card.summary.end_date)}`,
      paymentSummary: card.summary.payment_summary,
    });
  }

  closeEditGroup(): void {
    if (this.isSavingGroupReservation()) return;
    this.editingGroup.set(null);
    this.groupReasonError.set(false);
    this.groupEditExtrasMap = {};
  }

  isGroupEditExtraSelected(extraId: number): boolean {
    return extraId in this.groupEditExtrasMap;
  }

  toggleGroupEditExtra(extraId: number, event: Event): void {
    if ((event.target as HTMLInputElement).checked) this.groupEditExtrasMap[extraId] = 1;
    else delete this.groupEditExtrasMap[extraId];
  }

  updateGroupEditExtraQuantity(extraId: number, value: unknown): void {
    const quantity = Number(value);
    if (Number.isInteger(quantity) && quantity > 0) this.groupEditExtrasMap[extraId] = quantity;
  }

  async saveGroupReservation(): Promise<void> {
    const group = this.editingGroup();
    if (!group) return;
    if (!validateAndFocusFirstError(this.groupEditFormRef, this.groupEditFormEl?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de guardar los cambios.')) {
      return;
    }
    if (this.groupEditForm.start_time >= this.groupEditForm.end_time) {
      return focusFieldWithError(this.groupEditFormEl?.nativeElement, '[name="groupEditEnd"]', msg => this.notifications.error(msg), 'La hora de finalización debe ser posterior a la hora de inicio');
    }
    const space = this.selectedGroupEditSpace();
    const payload: UpdateReservationGroupRequest = {
      start_time: this.groupEditForm.start_time,
      end_time: this.groupEditForm.end_time,
      number_of_people: Number(this.groupEditForm.number_of_people),
      status: this.groupEditForm.status,
      guest_name: group.primary.is_guest || group.primary.guest_name ? String(this.groupEditForm.guest_name || '').trim() : undefined,
      guest_phone: String(this.groupEditForm.guest_phone || '').trim() || undefined,
      guest_email: group.primary.is_guest || group.primary.guest_email ? String(this.groupEditForm.guest_email || '').trim() : undefined,
      extras: Object.entries(this.groupEditExtrasMap).map(([extraId, quantity]) => ({ extra_id: Number(extraId), quantity })),
      ...(this.groupEditForm.custom_price_total !== '' && this.groupEditForm.custom_price_total !== null && this.groupEditForm.custom_price_total !== undefined
        ? { custom_price_total: this.groupEditForm.custom_price_total }
        : {}),
      change_reason: String(this.groupEditForm.change_reason || '').trim(),
      // El conjunto ya estaba pagado (por la plataforma o por un enlace de
      // pago) al abrir este formulario -- nunca reenviar como confirmación
      // de pago externo solo porque payment_status siga en 'paid' en el
      // formulario (así se inicializa siempre que ya estaba pagado, sin
      // importar el origen). Ver el comentario en externalPaymentPayload.
      ...this.externalPaymentPayload(this.groupEditForm, group.paymentSummary === 'paid'),
    };
    if (Number(payload.number_of_people) > Number(space?.capacity || 100)) {
      return focusFieldWithError(this.groupEditFormEl?.nativeElement, '[name="groupEditPeople"]', msg => this.notifications.error(msg), `La cantidad de personas supera la capacidad del espacio (${space?.capacity}).`);
    }
    this.isSavingGroupReservation.set(true);
    try {
      const updated = await this.dialogs.confirmAndRun(
        {
          title: 'Confirmar actualización del conjunto',
          message: `¿Aplicas este cambio a las ${group.dateCount} fechas de la reserva y notificas al cliente?`,
          tone: 'info',
          confirmText: 'Guardar y notificar',
        },
        () => this.reservationService.actualizarReservacionGrupo(group.groupId, payload),
        result => result.payment_difference_link_url
          ? {
              tone: 'info',
              message: result.msg,
              actionLabel: 'Ver enlace de pago de la diferencia',
              actionUrl: result.payment_difference_link_url,
            }
          : 'Conjunto actualizado y notificación procesada',
        error => this.getFriendlyError(error, 'No se pudo actualizar el conjunto de reservas.'),
      );
      if (updated !== null) {
        this.editingGroup.set(null);
        this.cargarReservaciones();
        this.refreshGroupDetailIfOpen(group.groupId);
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.isSavingGroupReservation.set(false);
    }
  }

  async deleteReservationGroup(card: { groupId: string; summary: NonNullable<Reservation['group_summary']> }): Promise<void> {
    const groupId = card.groupId;
    if (!groupId) return;
    if (!this.cooldown.consume('delete-reservation-group')) return;
    if (this.cancellingGroupId() !== null) return;
    this.cancellingGroupId.set(groupId);
    const dateCount = card.summary.date_count;
    const dateRangeLabel = dateCount === 1 ? this.formatDate(card.summary.start_date) : `${this.formatDate(card.summary.start_date)} – ${this.formatDate(card.summary.end_date)}`;
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: `Enviar el conjunto de ${dateCount} fechas a la Papelera`,
          message: `Se cancelarán las ${dateCount} fechas de esta reserva (${dateRangeLabel}) y se conservarán durante 30 días en la Papelera.`,
          confirmText: 'Cancelar todo el conjunto',
          danger: true,
          fields: [
            { name: 'reason', label: 'Motivo de la cancelación', type: 'textarea', required: true, placeholder: 'Ej: El cliente solicitó cambiar la fecha de su evento' },
            { name: 'notify', label: 'Notificar al cliente por correo', type: 'checkbox', value: false, hint: 'Desactivado: el cliente no se enterará de la cancelación. Actívalo para enviarle un correo explicando el motivo.' },
          ],
        },
        values => this.reservationService.eliminarReservacionGrupo(
          groupId,
          String(values['reason'] || '').trim(),
          values['notify'] === true,
        ),
        'El conjunto de reservas fue cancelado y enviado a la Papelera',
        error => this.getFriendlyError(error, 'No se pudo enviar el conjunto a la papelera.'),
      );
      if (result !== null) {
        this.cargarReservaciones();
        this.refreshGroupDetailIfOpen(groupId);
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.cancellingGroupId.set(null);
    }
  }

  // ---------------------------------------------------------------------
  // Modal "ver fechas del conjunto"
  // ---------------------------------------------------------------------

  openGroupDetail(card: { groupId: string; primary: Reservation }): void {
    this.groupDetailModal.set({ groupId: card.groupId, spaceTitle: this.getReservationSpaceTitle(card.primary) });
    this.groupDetailSearch.set('');
    this.groupDetailPageSize.set(25);
    this.loadGroupDetailPage(1);
  }

  closeGroupDetail(): void {
    this.groupDetailModal.set(null);
    this.groupDetailReservations.set([]);
  }

  private loadGroupDetailPage(page: number): void {
    const modal = this.groupDetailModal();
    if (!modal) return;
    this.groupDetailLoading.set(true);
    this.reservationService.getReservationGroupDates(modal.groupId, {
      page,
      limit: this.groupDetailPageSize(),
      q: this.groupDetailSearch() || undefined,
    }).subscribe({
      next: response => {
        this.groupDetailReservations.set(response.reservations || []);
        this.groupDetailTotal.set(Number(response.count || 0));
        this.groupDetailPages.set(Math.max(1, Number(response.pages || 1)));
        this.groupDetailPage.set(Math.max(1, Number(response.page || page)));
        this.groupDetailLoading.set(false);
      },
      error: error => {
        this.groupDetailReservations.set([]);
        this.groupDetailTotal.set(0);
        this.groupDetailPages.set(1);
        this.groupDetailLoading.set(false);
        this.notifications.error(this.getFriendlyError(error, 'No fue posible cargar las fechas del conjunto.'));
      },
    });
  }

  /** Si el modal de detalle está abierto para ESTE grupo, refresca su página actual -- tras editar/cancelar una fecha desde dentro o fuera del modal. */
  private refreshGroupDetailIfOpen(groupId: string): void {
    if (this.groupDetailModal()?.groupId === groupId) this.loadGroupDetailPage(this.groupDetailPage());
  }

  goToGroupDetailPage(page: number): void {
    if (this.groupDetailLoading() || page < 1 || page > this.groupDetailPages() || page === this.groupDetailPage()) return;
    this.loadGroupDetailPage(page);
  }

  onGroupDetailSearchChange(value: string): void { this.groupDetailSearch.set(String(value || '').trimStart()); }
  onGroupDetailPageSizeChange(value: number): void {
    this.groupDetailPageSize.set(Number(value) || 25);
    this.loadGroupDetailPage(1);
  }

  applyGroupDetailFilters(): void {
    this.loadGroupDetailPage(1);
  }

  resetGroupDetailFilters(): void {
    this.groupDetailSearch.set('');
    this.loadGroupDetailPage(1);
  }

  async saveReservation(): Promise<void> {
    const reservation = this.editingReservation();
    const space = this.selectedEditSpace();
    const reason = String(this.editForm.change_reason || '').trim();
    if (!reservation || !space) return focusFieldWithError(this.editFormEl?.nativeElement, '[name="editSpace"]', msg => this.notifications.error(msg), 'Selecciona un espacio válido');
    if (!validateAndFocusFirstError(this.editFormRef, this.editFormEl?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de guardar los cambios.')) {
      return;
    }
    if (this.editForm.start_time >= this.editForm.end_time) {
      return focusFieldWithError(this.editFormEl?.nativeElement, '[name="editEnd"]', msg => this.notifications.error(msg), 'La hora de finalización debe ser posterior a la hora de inicio');
    }
    if (this.editSelectedOpeningTime && this.editSelectedClosingTime && (this.editForm.start_time < this.editSelectedOpeningTime || this.editForm.end_time > this.editSelectedClosingTime)) {
      return focusFieldWithError(this.editFormEl?.nativeElement, '[name="editStart"]', msg => this.notifications.error(msg), `Selecciona un horario entre ${this.editSelectedOpeningTime} y ${this.editSelectedClosingTime}`);
    }
    if (!Number.isInteger(Number(this.editForm.number_of_people)) || Number(this.editForm.number_of_people) < 1 || Number(this.editForm.number_of_people) > Number(space.capacity)) {
      return focusFieldWithError(this.editFormEl?.nativeElement, '[name="editPeople"]', msg => this.notifications.error(msg), `La cantidad de personas debe estar entre 1 y ${space.capacity}`);
    }
    const payload: UpdateReservationRequest = {
      space_id: Number(this.editForm.space_id),
      reservation_date: this.editForm.reservation_date,
      start_time: this.editForm.start_time,
      end_time: this.editForm.end_time,
      number_of_people: Number(this.editForm.number_of_people),
      status: this.editForm.status,
      guest_name: (reservation.is_guest || reservation.guest_name) ? String(this.editForm.guest_name || '').trim() : undefined,
      guest_phone: String(this.editForm.guest_phone || '').trim() || undefined,
      guest_email: (reservation.is_guest || reservation.guest_email) ? String(this.editForm.guest_email || '').trim() : undefined,
      extras: Object.entries(this.editExtrasMap).map(([extraId, quantity]) => ({ extra_id: Number(extraId), quantity })),
      change_reason: reason,
      ...this.externalPaymentPayload(this.editForm, reservation.payment_status === 'paid' && reservation.payment_origin !== 'external'),
    };
    this.isSavingReservation.set(true);
    try {
      const updated = await this.dialogs.confirmAndRun(
        { title: 'Confirmar actualización', message: '¿Guardas los cambios y notificas al cliente?', tone: 'info', confirmText: 'Guardar y notificar' },
        () => this.reservationService.actualizarReservacion(reservation.reservation_id, payload),
        'Reservación actualizada y notificación procesada',
        error => this.getFriendlyError(error, 'No se pudo actualizar la reservación.'),
      );
      if (updated !== null) {
        this.editingReservation.set(null);
        this.cargarReservaciones();
        if (reservation.reservation_group_id) this.refreshGroupDetailIfOpen(reservation.reservation_group_id);
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.isSavingReservation.set(false);
    }
  }

  async deleteReservation(reservation: Reservation): Promise<void> {
    if (!this.cooldown.consume('delete-reservation')) return;
    if (this.cancellingId() !== null) return;
    this.cancellingId.set(reservation.reservation_id);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: `Enviar reserva ${this.getReservationReference(reservation)} a la Papelera`,
          message: 'La reserva se cancelará y se conservará durante 30 días en la Papelera.',
          confirmText: 'Cancelar y enviar a la Papelera',
          danger: true,
          fields: [
            { name: 'reason', label: 'Motivo de la cancelación', type: 'textarea', required: true, placeholder: 'Ej: El cliente solicitó cambiar la fecha de su evento' },
            { name: 'notify', label: 'Notificar al cliente por correo', type: 'checkbox', value: false, hint: 'Desactivado: el cliente no se enterará de la cancelación. Actívalo para enviarle un correo explicando el motivo.' },
          ],
        },
        values => this.reservationService.eliminarReservacion(
          reservation.reservation_id,
          String(values['reason'] || '').trim(),
          values['notify'] === true,
        ),
        'Reserva cancelada y enviada a la Papelera',
        error => this.getFriendlyError(error, 'No se pudo enviar la reserva a la papelera.'),
      );
      if (result !== null) {
        this.cargarReservaciones();
        if (reservation.reservation_group_id) this.refreshGroupDetailIfOpen(reservation.reservation_group_id);
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.cancellingId.set(null);
    }
  }

  /** Tono semántico de una reserva en el listado (solo presentación). */
  reservationTone(status?: string | null): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
    switch ((status || '').toLowerCase()) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'danger';
      case 'completed': return 'info';
      default: return 'neutral';
    }
  }

  sendPaymentLink(reservation: Reservation): void {
    const email = this.getReservationCustomerEmail(reservation);
    this.linkCopied.set(false);
    // Reactivar una reserva CANCELADA (el backend revalida que el horario
    // siga libre antes de tocar nada -- si alguien más ya lo tomó, rechaza
    // la acción entera y no envía ningún correo) es distinto de solo
    // reenviar el enlace de una que sigue pendiente -- se avisa aparte para
    // que el admin sepa qué va a pasar antes de confirmar.
    const isReactivation = reservation.status === 'cancelled';
    const baseMessage = email ? `Se enviará un correo con el enlace de pago a ${email}.` : 'Se enviará el enlace de pago al cliente de esta reserva.';
    // 'individual': esta acción es siempre "solo esta fecha", aunque
    // pertenezca a un conjunto (el botón "enviar enlace del conjunto"
    // completo vive en la tarjeta de grupo, ver sendGroupPaymentLink).
    this.sendLinkModal.set({
      title: isReactivation ? 'Reactivar reserva y enviar enlace de pago' : 'Enviar enlace de pago',
      message: isReactivation
        ? `Esta reserva está cancelada. Antes de enviar el enlace se revalidará que el horario siga libre -- si otro cliente ya lo tomó, no se enviará nada y la reserva seguirá cancelada. Si sigue libre, volverá a quedar "Pendiente" (bloqueando el horario de nuevo) mientras el cliente paga. ${baseMessage}`
        : baseMessage,
      reservationId: reservation.reservation_id,
      scope: 'individual',
    });
  }

  // El backend (resendReservationPaymentLink) ya resuelve TODO el grupo a
  // partir de cualquier reservation_id que le pertenezca, excluyendo del
  // monto las fechas que ya estén pagadas -- basta con llamarlo con la
  // fecha "primary" de la tarjeta. Antes esta acción solo existía por fecha
  // individual dentro del desplegable, sin forma de reenviar el enlace del
  // conjunto completo desde la tarjeta de grupo.
  sendGroupPaymentLink(card: { groupId: string; primary: Reservation }): void {
    if (this.sendingGroupLinkId() !== null) return;
    const email = this.getReservationCustomerEmail(card.primary);
    this.linkCopied.set(false);
    this.sendLinkModal.set({
      title: 'Enviar enlace de pago del conjunto',
      message: email
        ? `Se enviará un correo con el enlace de pago (solo de las fechas que sigan pendientes) a ${email}.`
        : 'Se enviará el enlace de pago de las fechas pendientes al cliente de este conjunto.',
      reservationId: card.primary.reservation_id,
      scope: 'group',
      groupId: card.groupId,
    });
  }

  closeSendPaymentLinkModal(): void {
    if (this.sendLinkBusy()) return;
    if (this.linkCopiedTimer) clearTimeout(this.linkCopiedTimer);
    this.sendLinkModal.set(null);
    this.linkCopied.set(false);
  }

  confirmSendPaymentLink(): void {
    const target = this.sendLinkModal();
    if (!target || this.sendLinkBusy()) return;
    this.sendLinkBusy.set(true);
    if (target.scope === 'group' && target.groupId) this.sendingGroupLinkId.set(target.groupId);
    this.reservationService.resendPaymentLink(target.reservationId, target.scope).subscribe({
      next: response => {
        this.sendLinkBusy.set(false);
        if (target.scope === 'group') this.sendingGroupLinkId.set(null);
        if (response.status === 'already_paid') {
          this.notifications.show(response.msg || (target.scope === 'group' ? 'Todo el conjunto ya fue pagado.' : 'Esta reserva ya fue pagada.'), 'info');
        } else {
          this.notifications.success(response.msg || (target.scope === 'group' ? 'Enlace de pago del conjunto enviado correctamente.' : 'Enlace de pago enviado correctamente.'));
        }
        this.sendLinkModal.set(null);
      },
      error: error => {
        this.sendLinkBusy.set(false);
        if (target.scope === 'group') this.sendingGroupLinkId.set(null);
        this.notifications.error(this.getFriendlyError(error, target.scope === 'group' ? 'No fue posible enviar el enlace de pago del conjunto.' : 'No fue posible enviar el enlace de pago.'));
      },
    });
  }

  // Respaldo por si el cliente tiene problemas para recibir el correo: copia
  // el mismo enlace que se acaba de (re)generar para compartirlo a mano
  // (WhatsApp u otra red). NO envía correo (notify=false) -- copiar es una
  // vía alterna al correo, no un envío adicional.
  copyPaymentLink(): void {
    const target = this.sendLinkModal();
    if (!target || this.sendLinkBusy()) return;
    this.sendLinkBusy.set(true);
    if (target.scope === 'group' && target.groupId) this.sendingGroupLinkId.set(target.groupId);
    // notify=false: solo se obtiene/resincroniza el enlace, nunca se envía
    // el correo -- copiar es una vía alterna al correo, no un envío extra.
    this.reservationService.resendPaymentLink(target.reservationId, target.scope, false).subscribe({
      next: async response => {
        this.sendLinkBusy.set(false);
        if (target.scope === 'group') this.sendingGroupLinkId.set(null);
        if (!response.paymentLinkUrl) {
          this.notifications.error('No fue posible obtener el enlace de pago.');
          return;
        }
        try {
          await navigator.clipboard.writeText(response.paymentLinkUrl);
          this.linkCopied.set(true);
          if (this.linkCopiedTimer) clearTimeout(this.linkCopiedTimer);
          this.linkCopiedTimer = setTimeout(() => this.linkCopied.set(false), 2200);
          this.notifications.success(
            response.status === 'already_paid'
              ? 'Enlace copiado. Esta reserva ya estaba pagada.'
              : 'Enlace copiado al portapapeles.',
          );
        } catch {
          this.notifications.error(`No fue posible copiar automáticamente. Enlace: ${response.paymentLinkUrl}`);
        }
      },
      error: error => {
        this.sendLinkBusy.set(false);
        if (target.scope === 'group') this.sendingGroupLinkId.set(null);
        this.notifications.error(this.getFriendlyError(error, 'No fue posible obtener el enlace de pago.'));
      },
    });
  }

  cancelForm(): void {
    this.resetForm();
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  getReservationReference(reservation: Pick<Reservation, 'reservation_id' | 'business_reservation_number'> | null | undefined): string {
    const number = Number(reservation?.business_reservation_number || reservation?.reservation_id || 0);
    return number > 0 ? `R-${String(number).padStart(4, '0')}` : 'R-PENDIENTE';
  }

  getReservationCustomerName(reservation: Reservation): string {
    return reservation.customer?.name
      || reservation.customer_name
      || reservation.guest_name
      || 'Invitado';
  }

  getReservationSpaceTitle(reservation: Reservation): string {
    return reservation.space?.title || reservation.space_title || 'Espacio sin nombre';
  }

  getReservationCustomerEmail(reservation: Reservation): string {
    return reservation.customer?.email
      || reservation.customer_email
      || reservation.guest_email
      || 'Sin dato';
  }

  getReservationCustomerPhone(reservation: Reservation): string {
    return reservation.customer?.phone_number
      || reservation.customer_phone
      || reservation.guest_phone
      || 'Sin dato';
  }

  getEstadoText(estado: string): string {
    const estados: { [key: string]: string } = {
      'confirmed': 'Confirmada',
      'pending': 'Pendiente',
      'cancelled': 'Cancelada',
      'completed': 'Completada',
    };
    return estados[estado.toLowerCase()] || estado;
  }

  private resetForm(): void {
    this.reservationForm = {
      user_id: null,
      space_id: null,
      reservation_date: '',
      reservation_dates: [] as string[],
      start_time: '',
      end_time: '',
      number_of_people: 1,
      guest_name: '',
      guest_phone: '',
      guest_email: '',
      extras: []
    };
    this.selectedExtrasMap = {};
    this.availableDays = undefined;
    this.selectedSpaceSchedules = [];
    this.resetSelectedSchedule();
    this.isScheduleLoading.set(false);
    this.showCreateForm.set(false);
    this.reservationType = 'user';
    this.multiDateMode = false;
    this.reservationDatesInput = '';
    this.multiDateScheduleError = null;
    this.multiDateEstimatedPricePerDay = 0;
  }

  private getFriendlyError(error: any, fallback: string): string {
    return friendlyError(error, fallback);
  }

}
